import L from "leaflet";

const STATE_KEY = "hydrotools:live-traffic:v1";
const AIS_DB_BASE_PATH = "/api/ais";
const AIS_DB_LATEST_PATH = `${AIS_DB_BASE_PATH}/v1/vessels/latest`;
const AIS_DB_TRACKS_PATH = `${AIS_DB_BASE_PATH}/v1/vessels/tracks`;
const AIS_DB_METRICS_PATH = `${AIS_DB_BASE_PATH}/v1/vessels/metrics`;
const MAX_TRACK_RENDER_POINTS = 2500;
const OPENSKY_PROXY_PATH = "/api/live-traffic/opensky";
const ADSB_PROXY_PREFIX = "/api/live-traffic/adsb";
const MAX_LOG_LINES = 500;
const MAX_VESSEL_CACHE = 5000;
const VESSEL_POLL_INTERVAL_MS = 1000;
const AIR_POLL_INTERVAL_MS = 15000;
const MAX_AIR_TARGETS = 800;
const MAX_VESSEL_TARGETS = 800;
const ACTIVE_MOVEMENT_NM_THRESHOLD = 0.1;
const AIS_HEALTH_POLL_INTERVAL_MS = 60000;

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function readState(stateKey) {
  try {
    const raw = localStorage.getItem(stateKey);
    const x = raw ? JSON.parse(raw) : {};
    return {
      useMapBounds: true,
      vesselScope: typeof x.vesselScope === "string" ? x.vesselScope : "regional-us",
      vesselFilterText: typeof x.vesselFilterText === "string" ? x.vesselFilterText : "",
      refreshSec: AIR_POLL_INTERVAL_MS / 1000,
      maxAirTargets: MAX_AIR_TARGETS,
      maxVesselTargets: MAX_VESSEL_TARGETS,
      vesselColorMode: x.vesselColorMode === "movement" ? "movement" : "identity",
      vesselMoveWindowMin: Number.isFinite(Number(x.vesselMoveWindowMin)) ? clamp(Math.round(Number(x.vesselMoveWindowMin)), 5, 360) : 60,
      vesselActivityFilter: x.vesselActivityFilter === "active" ? "active" : "all",
      vesselLayerOpacity: Number.isFinite(Number(x.vesselLayerOpacity)) ? clamp(Number(x.vesselLayerOpacity), 0.1, 1) : 1,
      vesselTrackHours: Number.isFinite(Number(x.vesselTrackHours)) ? clamp(Math.round(Number(x.vesselTrackHours)), 1, 48) : 12,
      vesselTrackRenderMode: x.vesselTrackRenderMode === "full" ? "full" : "decimated",
      openskyUsername: typeof x.openskyUsername === "string" ? x.openskyUsername : "",
      openskyPassword: typeof x.openskyPassword === "string" ? x.openskyPassword : "",
      trackOnClick: x.trackOnClick !== false,
      sectionOpen: {
        controls: x?.sectionOpen?.controls === true,
        vesselTable: x?.sectionOpen?.vesselTable === true,
        trackedVessels: x?.sectionOpen?.trackedVessels === true,
        trackedFlights: x?.sectionOpen?.trackedFlights === true,
        diagnostics: x?.sectionOpen?.diagnostics === true,
      },
    };
  } catch {
    return {
      useMapBounds: true,
      vesselScope: "regional-us",
      vesselFilterText: "",
      refreshSec: AIR_POLL_INTERVAL_MS / 1000,
      maxAirTargets: MAX_AIR_TARGETS,
      maxVesselTargets: MAX_VESSEL_TARGETS,
      vesselColorMode: "identity",
      vesselMoveWindowMin: 60,
      vesselActivityFilter: "all",
      vesselLayerOpacity: 1,
      vesselTrackHours: 12,
      vesselTrackRenderMode: "decimated",
      openskyUsername: "",
      openskyPassword: "",
      trackOnClick: true,
      sectionOpen: {
        controls: false,
        vesselTable: false,
        trackedVessels: false,
        trackedFlights: false,
        diagnostics: false,
      },
    };
  }
}

function saveState(stateKey, s) {
  try {
    localStorage.setItem(stateKey, JSON.stringify(s));
  } catch {}
}

function readLog(logKey) {
  try {
    const raw = localStorage.getItem(logKey);
    const rows = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(rows)) return [];
    return rows.slice(-MAX_LOG_LINES);
  } catch {
    return [];
  }
}

function saveLog(logKey, rows) {
  try {
    localStorage.setItem(logKey, JSON.stringify(Array.isArray(rows) ? rows.slice(-MAX_LOG_LINES) : []));
  } catch {}
}

function readVesselCache(vesselCacheKey) {
  try {
    const raw = localStorage.getItem(vesselCacheKey);
    const items = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(items)) return [];
    return items
      .filter((x) => x && Number.isFinite(Number(x.lat)) && Number.isFinite(Number(x.lon)) && String(x.id || "").trim())
      .slice(-MAX_VESSEL_CACHE);
  } catch {
    return [];
  }
}

function saveVesselCache(vesselCacheKey, items) {
  try {
    const clean = Array.isArray(items) ? items.slice(-MAX_VESSEL_CACHE) : [];
    localStorage.setItem(vesselCacheKey, JSON.stringify(clean));
  } catch {}
}

function kmhToKn(vKmh) {
  return Number.isFinite(vKmh) ? vKmh * 0.539957 : null;
}

function toFixed(v, d = 1) {
  return Number.isFinite(v) ? Number(v).toFixed(d) : "--";
}

function hashColor(seed) {
  const s = String(seed || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) - h) + s.charCodeAt(i);
  const hue = Math.abs(h) % 360;
  return {
    hue,
    fill: `hsla(${hue}, 58%, 82%, 0.45)`,
    border: `hsl(${hue} 24% 32%)`,
    accent: `hsla(${hue}, 54%, 84%, 0.26)`,
  };
}

function movementColor(movementNm) {
  const n = Number(movementNm);
  if (!Number.isFinite(n) || n <= 0.1) {
    return {
      fill: "hsla(210, 14%, 58%, 0.5)",
      border: "hsl(210 16% 32%)",
      accent: "hsla(210, 14%, 58%, 0.2)",
      label: "stationary",
    };
  }
  if (n <= 1) {
    return {
      fill: "hsla(145, 58%, 63%, 0.5)",
      border: "hsl(145 44% 28%)",
      accent: "hsla(145, 58%, 63%, 0.22)",
      label: "low movement",
    };
  }
  if (n <= 5) {
    return {
      fill: "hsla(36, 85%, 62%, 0.5)",
      border: "hsl(36 62% 30%)",
      accent: "hsla(36, 85%, 62%, 0.22)",
      label: "transit",
    };
  }
  return {
    fill: "hsla(5, 78%, 62%, 0.55)",
    border: "hsl(5 58% 32%)",
    accent: "hsla(5, 78%, 62%, 0.24)",
    label: "high movement",
  };
}

function buildSameOriginUrl(path) {
  try {
    const loc = window?.location;
    if (!loc?.host || !loc?.protocol?.startsWith("http")) return "";
    return `${loc.protocol}//${loc.host}${path}`;
  } catch {
    return "";
  }
}

function getAisDbApiBase() {
  return buildSameOriginUrl(AIS_DB_BASE_PATH) || AIS_DB_BASE_PATH;
}

function formatRelativeTimeFromIso(isoLike) {
  const ms = Date.parse(String(isoLike || ""));
  if (!Number.isFinite(ms)) return "--";
  const diffMs = Math.max(0, Date.now() - ms);
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  return `${hr}h ago`;
}

function formatCompactCount(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "--";
  return v.toLocaleString();
}

function makeAirIcon(headingDeg = 0, onGround = false) {
  const hdg = Number.isFinite(Number(headingDeg)) ? Number(headingDeg) : 0;
  const grounded = !!onGround;
  return L.divIcon({
    className: "lt-aircraft-icon-wrap",
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    html: `
      <div class="lt-aircraft-icon ${grounded ? "is-ground" : ""}" style="transform: rotate(${hdg}deg);" data-hdg="${hdg}">
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M11.2 1.5h1.6l1.2 6.1 6.2 2.3v1.8l-6.2-0.6-1.2 11.4h-1.6l-1.2-11.4-6.2 0.6v-1.8l6.2-2.3z" />
        </svg>
      </div>
    `,
  });
}

function makeVesselIcon(headingDeg = 0, colors = hashColor("")) {
  const hdg = Number.isFinite(Number(headingDeg)) ? Number(headingDeg) : 0;
  // AIS COG is degrees clockwise from North; SVG points East at 0 deg.
  const visHdg = hdg - 90;
  const fill = colors?.fill || "hsla(200, 58%, 82%, 0.45)";
  const border = colors?.border || "hsl(210 24% 32%)";
  return L.divIcon({
    className: "lt-vessel-icon-wrap",
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    html: `
      <div class="lt-vessel-icon" style="transform: rotate(${visHdg}deg);" data-hdg="${hdg}">
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M4 8 L14 8 L19 12 L14 16 L4 16 Z" fill="${fill}" stroke="${border}" stroke-width="1.4" />
        </svg>
      </div>
    `,
  });
}

function updateVesselIconElement(marker, headingDeg, colors = hashColor("")) {
  const el = marker?.getElement?.();
  const iconEl = el?.querySelector?.(".lt-vessel-icon");
  const pathEl = el?.querySelector?.(".lt-vessel-icon path");
  if (!iconEl || !pathEl) return false;
  const hdg = Number.isFinite(Number(headingDeg)) ? Number(headingDeg) : 0;
  const visHdg = hdg - 90;
  iconEl.style.transform = `rotate(${visHdg}deg)`;
  iconEl.setAttribute("data-hdg", String(hdg));
  pathEl.setAttribute("fill", colors?.fill || "hsla(200, 58%, 82%, 0.45)");
  pathEl.setAttribute("stroke", colors?.border || "hsl(210 24% 32%)");
  pathEl.setAttribute("stroke-width", "1.4");
  return true;
}

function updateAirIconElement(marker, headingDeg, onGround, trackColor = "") {
  const el = marker?.getElement?.();
  const iconEl = el?.querySelector?.(".lt-aircraft-icon");
  if (!iconEl) return false;
  const hdg = Number.isFinite(Number(headingDeg)) ? Number(headingDeg) : 0;
  iconEl.style.transform = `rotate(${hdg}deg)`;
  iconEl.classList.toggle("is-ground", !!onGround);
  const tracked = !!trackColor;
  iconEl.classList.toggle("is-tracked", tracked);
  if (tracked) iconEl.style.setProperty("--lt-track-color", String(trackColor));
  else iconEl.style.removeProperty("--lt-track-color");
  iconEl.setAttribute("data-hdg", String(hdg));
  return true;
}

function makeTrafficTool({ id, name, mode }) {
  return {
    id,
    name,
    init(app) { this._rt = this._rt ?? createRuntime(app, mode); },
    destroy() { this._rt?.destroy?.(); this._rt = null; },
    renderPanel(app, containerEl) {
      if (!this._rt) this._rt = createRuntime(app, mode);
      this._rt.mount(containerEl);
    },
  };
}

export const faaAirTrafficTool = makeTrafficTool({
  id: "faa-air-traffic",
  name: "FAA Air Traffic",
  mode: "air",
});

export const aisVesselTrafficTool = makeTrafficTool({
  id: "ais-vessel-traffic",
  name: "AIS Vessel Traffic",
  mode: "vessel",
});

function createRuntime(app, mode = "vessel") {
  const airMode = mode === "air";
  const vesselMode = mode === "vessel";
  const modeKey = airMode ? "air" : "vessel";
  const stateKey = `${STATE_KEY}:${modeKey}`;
  const logKey = `${stateKey}:diaglog`;
  const vesselCacheKey = `${stateKey}:vessel-cache`;
  let containerEl = null;
  let state = readState(stateKey);
  let airRunning = false;
  let vesselRunning = false;
  let airTimer = null;
  let airLoading = false;
  let lastAirFetchAt = 0;
  let airBackoffSec = 0;
  let airBackoffUntil = 0;
  let airMoveDebounce = null;
  let vesselMoveDebounce = null;
  let vesselPollTimer = null;
  let vesselPollRetryTimer = null;
  let vesselPollInFlight = false;
  let vesselPollFailCount = 0;
  let vesselCacheFlushTimer = null;
  let status = "Idle.";
  let diagLog = readLog(logKey);
  let diagLiveFollow = true;
  let diagScrollTop = 0;
  let vesselMsgCount = 0;
  let vesselParsedCount = 0;
  let vesselDroppedCount = 0;
  let airCount = 0;
  let vesselCount = 0;
  let trackColorSeq = 0;
  let vesselTrackReqSeq = 0;
  let vesselTrackColorSeq = 0;
  let activeTrackedVesselId = "";
  let expandedTrackedVesselId = "";
  let vesselPlaybackTimer = null;
  let aisHealthTimer = null;
  let isScrubbingVesselTrack = false;
  let isAdjustingVesselOpacity = false;
  let lastVesselQueryBoxes = [];
  let aisHealth = {
    loading: vesselMode,
    ok: false,
    status: "unknown",
    latestCount: 0,
    pointsLastHour: 0,
    lastReceivedAt: "",
    lagSec: null,
    bucketMinutes: 15,
    continuityHours: 24,
    continuity: [],
    error: "",
    updatedAt: 0,
  };

  const airLayer = L.layerGroup();
  const airTrailLayer = L.layerGroup();
  const vesselLayer = L.layerGroup();
  const vesselTrackLayer = L.layerGroup();
  const airMarkers = new Map();
  const vesselMarkers = new Map();
  const vesselSeenAt = new Map();
  const vesselLatest = new Map();
  const trackedFlights = new Map();
  const trackedVessels = new Map();
  const airSnapshot = new Map();
  const TRACK_COLORS = ["#f4a8c8", "#c9b8f7", "#a9dff5", "#bdeacb", "#f6d1ad"];

  function formatBbox(box) {
    if (!Array.isArray(box) || box.length < 2) return "--";
    const sw = box[0];
    const ne = box[1];
    if (!Array.isArray(sw) || !Array.isArray(ne)) return "--";
    return `${toFixed(sw[0], 3)}, ${toFixed(sw[1], 3)} -> ${toFixed(ne[0], 3)}, ${toFixed(ne[1], 3)}`;
  }

  function renderVesselQueryBboxes(boxes) {
    if (!vesselMode) return;
    lastVesselQueryBoxes = Array.isArray(boxes) ? boxes : [];
  }

  function getAisHealthTone(statusValue) {
    if (statusValue === "healthy") {
      return {
        label: "Healthy",
        color: "#22c55e",
        accent: "rgba(34,197,94,.18)",
        border: "rgba(34,197,94,.45)",
      };
    }
    if (statusValue === "stale") {
      return {
        label: "Stale",
        color: "#f59e0b",
        accent: "rgba(245,158,11,.18)",
        border: "rgba(245,158,11,.45)",
      };
    }
    if (statusValue === "down") {
      return {
        label: "Down",
        color: "#ef4444",
        accent: "rgba(239,68,68,.18)",
        border: "rgba(239,68,68,.45)",
      };
    }
    return {
      label: "Checking",
      color: "#94a3b8",
      accent: "rgba(148,163,184,.18)",
      border: "rgba(148,163,184,.35)",
    };
  }

  function renderAisContinuityBar() {
    if (!Array.isArray(aisHealth.continuity) || !aisHealth.continuity.length) {
      return `<div class="small" style="margin-top:6px;">No continuity history yet.</div>`;
    }
    const maxCount = Math.max(1, ...aisHealth.continuity.map((bucket) => Number(bucket?.pointCount || 0)));
    const bars = aisHealth.continuity.map((bucket) => {
      const count = Number(bucket?.pointCount || 0);
      const healthy = !!bucket?.healthy;
      const intensity = healthy ? Math.max(0.18, Math.min(1, count / maxCount)) : 0.12;
      const color = healthy
        ? `rgba(34,197,94,${intensity.toFixed(3)})`
        : "rgba(239,68,68,.38)";
      const title = `${String(bucket?.bucketStart || "")} | ${count.toLocaleString()} msgs`;
      return `<div title="${esc(title)}" style="height:12px; border-radius:2px; background:${color}; border:1px solid rgba(15,23,42,.45);"></div>`;
    }).join("");
    return `
      <div style="margin-top:6px;">
        <div class="small" style="margin-bottom:4px;">Continuity (last ${Number(aisHealth.continuityHours || 24)}h)</div>
        <div style="display:grid; grid-template-columns:repeat(${aisHealth.continuity.length}, minmax(0, 1fr)); gap:2px;">
          ${bars}
        </div>
        <div class="small" style="margin-top:4px;">Green = messages received in bucket. Red = no activity.</div>
      </div>
    `;
  }

  async function fetchAisHealthMetrics() {
    if (!vesselMode) return;
    try {
      const res = await fetch(buildSameOriginUrl(AIS_DB_METRICS_PATH) || AIS_DB_METRICS_PATH);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json?.ok) throw new Error(String(json?.error || "metrics failed"));
      aisHealth = {
        loading: false,
        ok: true,
        status: String(json.status || "unknown"),
        latestCount: Number(json.latestCount || 0),
        pointsLastHour: Number(json.pointsLastHour || 0),
        lastReceivedAt: String(json.lastReceivedAt || ""),
        lagSec: Number.isFinite(Number(json.lagSec)) ? Number(json.lagSec) : null,
        bucketMinutes: Number(json.bucketMinutes || 15),
        continuityHours: Number(json.continuityHours || 24),
        continuity: Array.isArray(json.continuity) ? json.continuity : [],
        error: "",
        updatedAt: Date.now(),
      };
    } catch (err) {
      aisHealth = {
        ...aisHealth,
        loading: false,
        ok: false,
        status: "down",
        error: err?.message || String(err),
        updatedAt: Date.now(),
      };
    }
    renderSafe();
  }

  function startAisHealthPolling() {
    if (!vesselMode) return;
    if (aisHealthTimer) {
      clearInterval(aisHealthTimer);
      aisHealthTimer = null;
    }
    fetchAisHealthMetrics();
    aisHealthTimer = setInterval(fetchAisHealthMetrics, AIS_HEALTH_POLL_INTERVAL_MS);
  }

  function nowIso() {
    return new Date().toISOString();
  }

  function pushLog(level, message, meta = null) {
    const line = {
      ts: nowIso(),
      level: String(level || "info"),
      message: String(message || ""),
      meta: meta && typeof meta === "object" ? meta : null,
    };
    diagLog.push(line);
    if (diagLog.length > MAX_LOG_LINES) {
      diagLog = diagLog.slice(diagLog.length - MAX_LOG_LINES);
    }
    saveLog(logKey, diagLog);
  }

  function setStatus(next, { level = "info", log = true, meta = null } = {}) {
    status = String(next || "");
    if (log) pushLog(level, status, meta);
  }

  function shouldDeferPanelRender() {
    if (isScrubbingVesselTrack) return true;
    if (isAdjustingVesselOpacity) return true;
    if (!containerEl) return false;
    if (containerEl.querySelector(".lt-collapsible:hover")) return true;
    if (containerEl.querySelector("[data-lt-vtrack-scrubbar]:active")) return true;
    const active = document.activeElement;
    if (!active || !containerEl.contains(active)) return false;
    const tag = String(active.tagName || "").toUpperCase();
    // Avoid tearing down the panel while user is interacting with controls.
    return tag === "SELECT" || tag === "INPUT" || tag === "TEXTAREA";
  }

  function renderSafe() {
    if (shouldDeferPanelRender()) return;
    render();
  }

  // Update only the status line and vessel-info row in place, avoiding a full
  // innerHTML rebuild that would cause CSS hover-transform bouncing at 1 Hz.
  function updatePollStatusInplace() {
    if (!containerEl) return;
    const statusEl = containerEl.querySelector("[data-lt-status]");
    if (statusEl) statusEl.textContent = status;
    const infoEl = containerEl.querySelector("[data-lt-vessel-info]");
    if (infoEl) infoEl.innerHTML = `Vessel status: <strong>running</strong>, source: <strong>local db api</strong>, targets: <strong>${vesselCount.toLocaleString()}</strong>`;
    // Also refresh the vessel table section if it is open, since positions changed.
    const tableDetails = containerEl.querySelector("[data-lt-section='vesselTable']");
    if (tableDetails?.open) renderSafe();
  }

  function formatLog() {
    if (!diagLog.length) return "No diagnostics yet.";
    return diagLog.map((r) => {
      const suffix = r.meta ? ` | ${JSON.stringify(r.meta)}` : "";
      return `[${r.ts}] ${String(r.level || "info").toUpperCase()} ${r.message}${suffix}`;
    }).join("\n");
  }

  function formatAge(ms) {
    if (!Number.isFinite(Number(ms))) return "--";
    const n = Math.max(0, Math.round(Number(ms)));
    if (n < 1000) return `${n}ms`;
    return `${(n / 1000).toFixed(1)}s`;
  }

  function getVesselLayerOpacity() {
    return clamp(Number(state.vesselLayerOpacity || 1), 0.1, 1);
  }

  function isActiveVesselMovement(movementNm) {
    return Number.isFinite(Number(movementNm)) && Number(movementNm) > ACTIVE_MOVEMENT_NM_THRESHOLD;
  }

  function shouldDisplayLiveVessel(vesselLike) {
    if (state.vesselActivityFilter !== "active") return true;
    return isActiveVesselMovement(vesselLike?.movementNm);
  }

  function applyVesselLayerOpacity() {
    const opacity = getVesselLayerOpacity();
    vesselMarkers.forEach((marker) => {
      marker?.setOpacity?.(opacity);
    });
    rerenderAllTrackedVesselGeometries();
  }

  function syncVisibleVesselMarkersFromLatest() {
    const latest = Array.from(vesselLatest.values());
    for (let i = 0; i < latest.length; i += 1) {
      applyVesselTarget(latest[i], { fromCache: true });
    }
    vesselCount = vesselMarkers.size;
  }

  async function copyLogText(text) {
    const value = String(text || "");
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      return false;
    }
  }

  function downloadLogText(text) {
    const blob = new Blob([String(text || "")], { type: "text/plain;charset=utf-8" });
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = `live-traffic-diagnostics-${Date.now()}.log`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  }

  function mount(nextContainer) {
    containerEl = nextContainer;
    state.sectionOpen = {
      controls: false,
      vesselTable: false,
      trackedVessels: false,
      trackedFlights: false,
      diagnostics: false,
    };
    saveState(stateKey, state);
    const map = app.map.instance;
    if (airMode) {
      map.addLayer(airTrailLayer);
      map.addLayer(airLayer);
    }
    if (vesselMode) {
      map.addLayer(vesselLayer);
      map.addLayer(vesselTrackLayer);
      vesselPlaybackTimer = setInterval(tickTrackedVesselPlayback, 120);
      renderVesselQueryBboxes(buildVesselBoundingBoxes(map.getBounds()));
      startAisHealthPolling();
    }
    map.on("moveend", onMapMoveEnd);
    pushLog("info", `${airMode ? "Air" : "Vessel"} Traffic panel mounted.`);
    render();
  }

  function destroy() {
    if (airMode) stopAir();
    if (vesselMode) stopVessel();
    if (airMoveDebounce) clearTimeout(airMoveDebounce);
    if (vesselMoveDebounce) clearTimeout(vesselMoveDebounce);
    if (vesselCacheFlushTimer) clearTimeout(vesselCacheFlushTimer);
    if (vesselPlaybackTimer) {
      clearInterval(vesselPlaybackTimer);
      vesselPlaybackTimer = null;
    }
    if (aisHealthTimer) {
      clearInterval(aisHealthTimer);
      aisHealthTimer = null;
    }
    const map = app.map.instance;
    map.off("moveend", onMapMoveEnd);
    if (airMode) {
      map.removeLayer(airTrailLayer);
      map.removeLayer(airLayer);
      clearLayer(airLayer, airMarkers);
      clearTrailLayer();
    }
    if (vesselMode) {
      map.removeLayer(vesselLayer);
      map.removeLayer(vesselTrackLayer);
      clearLayer(vesselLayer, vesselMarkers);
      clearAllTrackedVessels();
    }
  }

  function onMapMoveEnd() {
    if (airMode && airRunning) {
      if (airMoveDebounce) clearTimeout(airMoveDebounce);
      airMoveDebounce = setTimeout(() => {
        if (!airRunning) return;
        const minGap = Math.max(2500, Math.round((AIR_POLL_INTERVAL_MS / 1000) * 850));
        if ((Date.now() - lastAirFetchAt) >= minGap) tickAir();
      }, 450);
    }
    if (vesselMode && vesselRunning && state.vesselScope === "map") {
      if (vesselMoveDebounce) clearTimeout(vesselMoveDebounce);
      vesselMoveDebounce = setTimeout(() => {
        if (!vesselRunning) return;
        pollVesselsOnce();
      }, 600);
    }
    if (vesselMode) {
      renderVesselQueryBboxes(buildVesselBoundingBoxes(app.map.instance.getBounds()));
    }
    render();
  }

  function clearLayer(layer, markerMap) {
    markerMap.forEach((m) => {
      try { layer.removeLayer(m); } catch {}
    });
    markerMap.clear();
  }

  function clearTrailLayer() {
    trackedFlights.forEach((t) => {
      if (t?.trailLine) {
        try { airTrailLayer.removeLayer(t.trailLine); } catch {}
      }
    });
  }

  function pullState() {
    if (!containerEl) return;
    state.useMapBounds = true;
    state.refreshSec = AIR_POLL_INTERVAL_MS / 1000;
    state.maxAirTargets = MAX_AIR_TARGETS;
    state.maxVesselTargets = MAX_VESSEL_TARGETS;
    if (airMode) {
      const openskyUserEl = containerEl.querySelector("#lt-opensky-user");
      const openskyPassEl = containerEl.querySelector("#lt-opensky-pass");
      const trackOnClickEl = containerEl.querySelector("#lt-track-on-click");
      if (openskyUserEl) state.openskyUsername = String(openskyUserEl.value || "").trim();
      if (openskyPassEl) state.openskyPassword = String(openskyPassEl.value || "").trim();
      if (trackOnClickEl) state.trackOnClick = !!trackOnClickEl.checked;
    }
    if (vesselMode) {
      const vesselScopeEl = containerEl.querySelector("#lt-vessel-scope");
      const vesselColorModeEl = containerEl.querySelector("#lt-vessel-color-mode");
      const vesselMoveWindowEl = containerEl.querySelector("#lt-vessel-move-window");
      const vesselActivityFilterEl = containerEl.querySelector("#lt-vessel-activity-filter");
      const vesselOpacityEl = containerEl.querySelector("#lt-vessel-opacity");
      const vesselTrackHoursEl = containerEl.querySelector("#lt-vessel-track-hours");
      const vesselTrackModeEl = containerEl.querySelector("#lt-vessel-track-mode");
      const vesselFilterEl = containerEl.querySelector("#lt-vessel-filter");
      if (vesselScopeEl) state.vesselScope = String(vesselScopeEl.value || state.vesselScope || "regional-us");
      if (vesselColorModeEl) state.vesselColorMode = String(vesselColorModeEl.value || "identity") === "movement" ? "movement" : "identity";
      if (vesselMoveWindowEl) state.vesselMoveWindowMin = clamp(Math.round(Number(vesselMoveWindowEl.value || state.vesselMoveWindowMin || 60)), 5, 360);
      if (vesselActivityFilterEl) state.vesselActivityFilter = String(vesselActivityFilterEl.value || "all") === "active" ? "active" : "all";
      if (vesselOpacityEl) state.vesselLayerOpacity = clamp(Number(vesselOpacityEl.value || (state.vesselLayerOpacity * 100) || 100) / 100, 0.1, 1);
      if (vesselTrackHoursEl) state.vesselTrackHours = clamp(Math.round(Number(vesselTrackHoursEl.value || state.vesselTrackHours || 12)), 1, 48);
      if (vesselTrackModeEl) state.vesselTrackRenderMode = String(vesselTrackModeEl.value || state.vesselTrackRenderMode || "decimated") === "full" ? "full" : "decimated";
      if (vesselFilterEl) state.vesselFilterText = String(vesselFilterEl.value || state.vesselFilterText || "");
    }
    saveState(stateKey, state);
  }

  async function tickAir() {
    if (!airRunning || airLoading) return;
    if (Date.now() < airBackoffUntil) {
      const waitSec = Math.max(1, Math.ceil((airBackoffUntil - Date.now()) / 1000));
      setStatus(`OpenSky throttled, retrying in ${waitSec}s.`, { level: "warn", log: false });
      render();
      return;
    }
    airLoading = true;
    render();
    try {
      const { items, provider } = await fetchAirTargets();
      syncAirMarkers(items.slice(0, MAX_AIR_TARGETS));
      airCount = airMarkers.size;
      lastAirFetchAt = Date.now();
      airBackoffSec = 0;
      airBackoffUntil = 0;
      setStatus(`Air updated: ${airCount.toLocaleString()} targets (${provider}).`, { log: false });
    } catch (err) {
      if (Number(err?.code) === 429) {
        airBackoffSec = airBackoffSec ? Math.min(180, Math.round(airBackoffSec * 1.8)) : 20;
        airBackoffUntil = Date.now() + (airBackoffSec * 1000);
        pushLog("warn", "OpenSky returned 429; backoff applied.", { backoffSec: airBackoffSec });
      }
      setStatus(`Air fetch failed: ${err?.message || String(err)}`, { level: "error" });
    } finally {
      airLoading = false;
      render();
    }
  }

  async function fetchOpenSkyTargets() {
    const map = app.map.instance;
    const baseUrl = buildSameOriginUrl(OPENSKY_PROXY_PATH) || "https://opensky-network.org/api/states/all";
    let url = baseUrl;
    if (map) {
      const b = map.getBounds();
      const q = new URLSearchParams({
        lamin: String(b.getSouth()),
        lomin: String(b.getWest()),
        lamax: String(b.getNorth()),
        lomax: String(b.getEast()),
      });
      url += `?${q.toString()}`;
    }
    const headers = {};
    if (state.openskyUsername && state.openskyPassword) {
      const token = btoa(`${state.openskyUsername}:${state.openskyPassword}`);
      headers.Authorization = `Basic ${token}`;
    }
    const res = await fetch(url, { headers });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      const err = new Error(`OpenSky HTTP ${res.status}${text ? ` - ${String(text).slice(0, 80)}` : ""}`);
      err.code = res.status;
      throw err;
    }
    const json = await res.json();
    const states = Array.isArray(json?.states) ? json.states : [];
    const out = [];
    for (let i = 0; i < states.length; i += 1) {
      const s = states[i];
      const lon = Number(s?.[5]);
      const lat = Number(s?.[6]);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      out.push({
        id: String(s?.[0] || `${lat},${lon},${i}`),
        callsign: String(s?.[1] || "").trim(),
        country: String(s?.[2] || "").trim(),
        velocityMs: Number(s?.[9]),
        heading: Number(s?.[10]),
        altitudeM: Number(s?.[7]),
        onGround: !!s?.[8],
        lat,
        lon,
      });
    }
    return out;
  }

  async function fetchAdsbLolTargets() {
    const map = app.map.instance;
    const b = map.getBounds();
    const c = b.getCenter();
    const latSpanKm = Math.max(1, (b.getNorth() - b.getSouth()) * 111.32);
    const lonScale = Math.max(0.2, Math.cos((c.lat * Math.PI) / 180));
    const lonSpanKm = Math.max(1, (b.getEast() - b.getWest()) * 111.32 * lonScale);
    const diagKm = Math.sqrt((latSpanKm * latSpanKm) + (lonSpanKm * lonSpanKm));
    const dist = clamp(Math.ceil(diagKm * 0.75), 60, 500);
    const qLat = Number(c.lat.toFixed(3));
    const qLon = Number(c.lng.toFixed(3));
    const directUrl = `https://api.adsb.lol/v2/lat/${qLat}/lon/${qLon}/dist/${dist}`;
    const proxiedUrl = buildSameOriginUrl(`${ADSB_PROXY_PREFIX}/v2/lat/${qLat}/lon/${qLon}/dist/${dist}`);
    let json = null;
    let lastErr = null;
    const candidates = [proxiedUrl, directUrl].filter(Boolean);
    for (let i = 0; i < candidates.length; i += 1) {
      const url = candidates[i];
      try {
        const res = await fetch(url);
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          const err = new Error(`ADSB HTTP ${res.status}${txt ? ` - ${String(txt).slice(0, 80)}` : ""}`);
          err.code = res.status;
          throw err;
        }
        json = await res.json();
        break;
      } catch (err) {
        lastErr = err;
      }
    }
    if (!json) throw lastErr || new Error("ADSB fetch failed");
    const ac = Array.isArray(json?.ac) ? json.ac : [];
    const out = [];
    for (let i = 0; i < ac.length; i += 1) {
      const a = ac[i];
      const lat = Number(a?.lat);
      const lon = Number(a?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      const gsKn = Number(a?.gs);
      const altFt = Number(a?.alt_baro);
      out.push({
        id: String(a?.hex || `${lat},${lon},${i}`),
        callsign: String(a?.flight || "").trim(),
        country: String(a?.r || "").trim(),
        velocityMs: Number.isFinite(gsKn) ? (gsKn / 1.94384) : null,
        heading: Number(a?.track),
        altitudeM: Number.isFinite(altFt) ? (altFt / 3.28084) : null,
        onGround: String(a?.alt_baro || "").toLowerCase() === "ground" || !!a?.ground,
        lat,
        lon,
      });
    }
    return out;
  }

  async function fetchAirTargets() {
    let openSkyErr = null;
    try {
      const items = await fetchOpenSkyTargets();
      return { items, provider: "OpenSky" };
    } catch (err) {
      openSkyErr = err;
    }
    const items = await fetchAdsbLolTargets();
    if (openSkyErr) {
      setStatus(`OpenSky unavailable (${openSkyErr?.message || "error"}), using ADSB fallback.`, { level: "warn", log: false });
      pushLog("warn", "Switched air provider to ADSB fallback.");
    }
    return { items, provider: "ADSB fallback" };
  }

  function syncAirMarkers(items) {
    const keep = new Set();
    const nextSnapshot = new Map();
    for (let i = 0; i < items.length; i += 1) {
      const t = items[i];
      keep.add(t.id);
      nextSnapshot.set(t.id, t);
      let marker = airMarkers.get(t.id);
      const tracked = trackedFlights.get(t.id);
      if (!marker) {
        marker = L.marker([t.lat, t.lon], {
          icon: makeAirIcon(t.heading, t.onGround),
          keyboard: false,
          riseOnHover: true,
        });
        marker.on("click", () => {
          if (!state.trackOnClick) return;
          toggleTrackedFlight(t.id);
        });
        marker.addTo(airLayer);
        airMarkers.set(t.id, marker);
      } else {
        marker.setLatLng([t.lat, t.lon]);
        if (!updateAirIconElement(marker, t.heading, t.onGround, tracked?.color || "")) {
          marker.setIcon(makeAirIcon(t.heading, t.onGround));
        }
      }
      if (!updateAirIconElement(marker, t.heading, t.onGround, tracked?.color || "")) {
        marker.setIcon(makeAirIcon(t.heading, t.onGround));
      }
      const velocityKn = Number.isFinite(t.velocityMs) ? t.velocityMs * 1.94384 : null;
      const altFt = Number.isFinite(t.altitudeM) ? t.altitudeM * 3.28084 : null;
      marker.bindPopup(
        `<div><strong>${esc(t.callsign || t.id)}</strong></div>
         <div class="small">Country: ${esc(t.country || "--")}</div>
         <div class="small">Speed: ${toFixed(velocityKn, 0)} kt</div>
         <div class="small">Heading: ${toFixed(t.heading, 0)} deg</div>
         <div class="small">Alt: ${toFixed(altFt, 0)} ft</div>
         <div class="small">Ground: ${t.onGround ? "yes" : "no"}</div>
         <div class="small">Tracked: ${tracked ? "yes" : "no"}</div>`,
      );
      if (tracked) {
        tracked.callsign = t.callsign || tracked.callsign || t.id;
        tracked.country = t.country || tracked.country || "";
        tracked.lat = t.lat;
        tracked.lon = t.lon;
        tracked.heading = t.heading;
        tracked.altitudeM = t.altitudeM;
        tracked.velocityMs = t.velocityMs;
        tracked.updatedAt = Date.now();
        appendTrackedPoint(tracked, t.lat, t.lon);
      }
    }
    airMarkers.forEach((marker, id) => {
      if (keep.has(id)) return;
      airLayer.removeLayer(marker);
      airMarkers.delete(id);
    });
    airSnapshot.clear();
    nextSnapshot.forEach((v, k) => airSnapshot.set(k, v));
  }

  function toggleTrackedFlight(id) {
    const current = trackedFlights.get(id);
    if (current) {
      if (current.trailLine) {
        try { airTrailLayer.removeLayer(current.trailLine); } catch {}
      }
      trackedFlights.delete(id);
      const marker = airMarkers.get(id);
      const snap = airSnapshot.get(id);
      if (marker && snap) {
        updateAirIconElement(marker, snap.heading, snap.onGround, "");
      }
      render();
      return;
    }
    const snap = airSnapshot.get(id);
    if (!snap) return;
    const color = TRACK_COLORS[trackColorSeq % TRACK_COLORS.length];
    trackColorSeq += 1;
    trackedFlights.set(id, {
      id,
      callsign: snap.callsign || id,
      country: snap.country || "",
      lat: snap.lat,
      lon: snap.lon,
      heading: snap.heading,
      altitudeM: snap.altitudeM,
      velocityMs: snap.velocityMs,
      color,
      updatedAt: Date.now(),
      trailLatLngs: [[snap.lat, snap.lon]],
      trailLine: L.polyline([[snap.lat, snap.lon]], {
        color,
        weight: 2,
        opacity: 0.9,
        dashArray: "2 7",
        lineCap: "round",
      }).addTo(airTrailLayer),
    });
    const marker = airMarkers.get(id);
    if (marker) {
      updateAirIconElement(marker, snap.heading, snap.onGround, color);
    }
    render();
  }

  function appendTrackedPoint(tracked, lat, lon) {
    if (!tracked || !Number.isFinite(lat) || !Number.isFinite(lon)) return;
    const points = Array.isArray(tracked.trailLatLngs) ? tracked.trailLatLngs : [];
    const prev = points.length ? points[points.length - 1] : null;
    if (prev) {
      const dLat = Math.abs(lat - prev[0]);
      const dLon = Math.abs(lon - prev[1]);
      if (dLat < 0.00008 && dLon < 0.00008) {
        return;
      }
    }
    points.push([lat, lon]);
    const maxPoints = 240;
    if (points.length > maxPoints) {
      points.splice(0, points.length - maxPoints);
    }
    tracked.trailLatLngs = points;
    if (!tracked.trailLine) {
      tracked.trailLine = L.polyline(points, {
        color: tracked.color || "#ff2d55",
        weight: 2,
        opacity: 0.9,
        dashArray: "2 7",
        lineCap: "round",
      }).addTo(airTrailLayer);
    } else {
      tracked.trailLine.setLatLngs(points);
      tracked.trailLine.setStyle({ color: tracked.color || "#ff2d55" });
    }
  }

  function zoomToTrackedFlight(id) {
    const t = trackedFlights.get(id);
    if (!t || !Number.isFinite(t.lat) || !Number.isFinite(t.lon)) return;
    const map = app.map.instance;
    const z = Math.max(Number(map.getZoom?.() || 9), 9);
    map.flyTo([t.lat, t.lon], z, { duration: 0.9 });
    const marker = airMarkers.get(id);
    if (marker) {
      setTimeout(() => marker.openPopup(), 350);
    }
  }

  function zoomToVessel(id) {
    const t = vesselLatest.get(String(id || ""));
    if (!t || !Number.isFinite(t.lat) || !Number.isFinite(t.lon)) return;
    const map = app.map.instance;
    const z = Math.max(Number(map.getZoom?.() || 9), 9);
    map.flyTo([t.lat, t.lon], z, { duration: 0.8 });
  }

  function zoomToTrackedVesselTracks() {
    const bounds = vesselTrackLayer.getBounds?.();
    if (bounds && bounds.isValid && bounds.isValid()) {
      app.map.instance.fitBounds(bounds.pad(0.12), { maxZoom: 12, animate: true, duration: 0.7 });
    }
  }

  function haversineNm(lat1, lon1, lat2, lon2) {
    const toRad = Math.PI / 180;
    const dLat = (lat2 - lat1) * toRad;
    const dLon = (lon2 - lon1) * toRad;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return 3440.065 * c;
  }

  function clearTrackedVesselGeometry(entry) {
    if (!entry) return;
    if (entry.trackLine) {
      try { vesselTrackLayer.removeLayer(entry.trackLine); } catch {}
      entry.trackLine = null;
    }
    if (entry.futureLine) {
      try { vesselTrackLayer.removeLayer(entry.futureLine); } catch {}
      entry.futureLine = null;
    }
    if (entry.startMarker) {
      try { vesselTrackLayer.removeLayer(entry.startMarker); } catch {}
      entry.startMarker = null;
    }
    if (entry.endMarker) {
      try { vesselTrackLayer.removeLayer(entry.endMarker); } catch {}
      entry.endMarker = null;
    }
    if (entry.playMarker) {
      try { vesselTrackLayer.removeLayer(entry.playMarker); } catch {}
      entry.playMarker = null;
    }
  }

  function clearAllTrackedVessels() {
    trackedVessels.forEach((entry) => clearTrackedVesselGeometry(entry));
    trackedVessels.clear();
    activeTrackedVesselId = "";
    expandedTrackedVesselId = "";
    vesselTrackLayer.clearLayers();
  }

  function createTrackedVesselEntry(mmsi, vesselName = "") {
    const id = String(mmsi || "").trim();
    if (!id) return null;
    const existing = trackedVessels.get(id);
    if (existing) return existing;
    const color = TRACK_COLORS[vesselTrackColorSeq % TRACK_COLORS.length];
    vesselTrackColorSeq += 1;
    const entry = {
      mmsi: id,
      name: String(vesselName || ""),
      color,
      hours: state.vesselTrackHours || 12,
      loading: false,
      error: "",
      points: 0,
      renderedPoints: 0,
      startAt: "",
      endAt: "",
      distanceNm: 0,
      avgSog: null,
      maxSog: null,
      allPoints: [],
      reqSeq: 0,
      trackedAt: Date.now(),
      trackLine: null,
      futureLine: null,
      startMarker: null,
      endMarker: null,
      playMarker: null,
      playback: {
        isPlaying: false,
        speed: 1,
        cursorIndex: 0,
        lastTickMs: 0,
        carry: 0,
      },
    };
    trackedVessels.set(id, entry);
    return entry;
  }

  function untrackVessel(mmsi) {
    const id = String(mmsi || "").trim();
    if (!id) return;
    const entry = trackedVessels.get(id);
    if (!entry) return;
    clearTrackedVesselGeometry(entry);
    trackedVessels.delete(id);
    if (expandedTrackedVesselId === id) expandedTrackedVesselId = "";
    if (activeTrackedVesselId === id) {
      let newest = "";
      let newestAt = -1;
      trackedVessels.forEach((x) => {
        const t = Number(x?.trackedAt || 0);
        if (t > newestAt) {
          newestAt = t;
          newest = String(x?.mmsi || "");
        }
      });
      activeTrackedVesselId = newest;
      rerenderAllTrackedVesselGeometries();
    }
  }

  function setActiveTrackedVessel(mmsi) {
    const id = String(mmsi || "").trim();
    if (!id || !trackedVessels.has(id)) return;
    if (activeTrackedVesselId === id) return;
    activeTrackedVesselId = id;
    rerenderAllTrackedVesselGeometries();
  }

  function toggleTrackedVesselExpanded(mmsi) {
    const id = String(mmsi || "").trim();
    if (!id || !trackedVessels.has(id)) return;
    if (expandedTrackedVesselId === id) {
      const current = trackedVessels.get(id);
      if (current?.playback) current.playback.isPlaying = false;
      expandedTrackedVesselId = "";
      render();
      return;
    }
    if (expandedTrackedVesselId) {
      const prev = trackedVessels.get(expandedTrackedVesselId);
      if (prev?.playback) prev.playback.isPlaying = false;
    }
    expandedTrackedVesselId = id;
    setActiveTrackedVessel(id);
    const entry = trackedVessels.get(id);
    if (entry && entry.allPoints.length) {
      entry.playback.cursorIndex = Math.max(0, entry.allPoints.length - 1);
      entry.playback.carry = 0;
      entry.playback.lastTickMs = Date.now();
      renderTrackedVesselGeometry(entry);
    }
    render();
  }

  function setTrackedVesselPlaybackSpeed(mmsi, speed) {
    const entry = trackedVessels.get(String(mmsi || ""));
    if (!entry) return;
    const s = Number(speed);
    entry.playback.speed = Number.isFinite(s) && s > 0 ? s : 1;
  }

  function setTrackedVesselPlaybackCursor(mmsi, index, { renderPanel = true, renderGeometry = true } = {}) {
    const entry = trackedVessels.get(String(mmsi || ""));
    if (!entry || !entry.allPoints.length) return;
    const n = clamp(Math.round(Number(index || 0)), 0, entry.allPoints.length - 1);
    entry.playback.cursorIndex = n;
    entry.playback.isPlaying = false;
    entry.playback.carry = 0;
    if (renderGeometry) renderTrackedVesselGeometry(entry);
    if (renderPanel) render();
  }

  function toggleTrackedVesselPlayback(mmsi) {
    const entry = trackedVessels.get(String(mmsi || ""));
    if (!entry || !entry.allPoints.length) return;
    if (entry.playback.cursorIndex >= entry.allPoints.length - 1) {
      entry.playback.cursorIndex = 0;
    }
    entry.playback.isPlaying = !entry.playback.isPlaying;
    entry.playback.lastTickMs = Date.now();
    entry.playback.carry = 0;
    render();
  }

  function tickTrackedVesselPlayback() {
    if (!trackedVessels.size) return;
    const now = Date.now();
    let changed = false;
    trackedVessels.forEach((entry) => {
      if (!entry?.playback?.isPlaying) return;
      const points = entry.allPoints;
      if (!Array.isArray(points) || points.length < 2) {
        entry.playback.isPlaying = false;
        return;
      }
      const last = entry.playback.lastTickMs || now;
      const dt = Math.max(1, now - last);
      entry.playback.lastTickMs = now;
      entry.playback.carry += (dt * Number(entry.playback.speed || 1)) / 500;
      let next = entry.playback.cursorIndex;
      while (entry.playback.carry >= 1) {
        entry.playback.carry -= 1;
        next += 1;
      }
      if (next >= points.length - 1) {
        next = points.length - 1;
        entry.playback.isPlaying = false;
        entry.playback.carry = 0;
      }
      if (next !== entry.playback.cursorIndex) {
        entry.playback.cursorIndex = next;
        renderTrackedVesselGeometry(entry);
        changed = true;
      }
    });
    if (changed) renderSafe();
  }

  function toggleTrackedVessel(mmsi, vesselName = "") {
    const id = String(mmsi || "").trim();
    if (!id) return;
    const existing = trackedVessels.get(id);
    if (existing) {
      untrackVessel(id);
      setStatus(`Untracked vessel ${id}.`, { log: false });
      render();
      return;
    }
    const entry = createTrackedVesselEntry(id, vesselName);
    if (!entry) return;
    activeTrackedVesselId = id;
    expandedTrackedVesselId = id;
    loadVesselTrack(entry);
  }

  function decimateTrackPoints(points, maxPoints = MAX_TRACK_RENDER_POINTS) {
    if (!Array.isArray(points) || points.length <= maxPoints) return Array.isArray(points) ? points : [];
    const step = Math.max(2, Math.ceil(points.length / maxPoints));
    const out = [points[0]];
    for (let i = step; i < points.length - 1; i += step) out.push(points[i]);
    out.push(points[points.length - 1]);
    return out;
  }

  function getRenderableTrackPoints(points) {
    if (!Array.isArray(points) || !points.length) return [];
    if (state.vesselTrackRenderMode === "full") return points;
    return decimateTrackPoints(points, MAX_TRACK_RENDER_POINTS);
  }

  function renderTrackedVesselGeometry(entry) {
    if (!entry) return [];
    const points = Array.isArray(entry.allPoints) ? entry.allPoints : [];
    clearTrackedVesselGeometry(entry);
    if (!Array.isArray(points) || !points.length) return [];
    const expanded = expandedTrackedVesselId === entry.mmsi;
    const cursor = clamp(
      Math.round(Number(entry.playback?.cursorIndex ?? (points.length - 1))),
      0,
      points.length - 1,
    );
    const renderPoints = getRenderableTrackPoints(points);
    const latLngs = renderPoints.map((p) => [p.lat, p.lon]);
    const isActive = !activeTrackedVesselId || activeTrackedVesselId === entry.mmsi;
    const layerOpacity = getVesselLayerOpacity();
    if (expanded) {
      const past = getRenderableTrackPoints(points.slice(0, cursor + 1));
      const future = getRenderableTrackPoints(points.slice(cursor));
      const pastLatLngs = past.map((p) => [p.lat, p.lon]);
      const futureLatLngs = future.map((p) => [p.lat, p.lon]);
      entry.trackLine = L.polyline(pastLatLngs, {
        color: entry.color,
        weight: 3.2,
        opacity: 0.98 * layerOpacity,
        lineCap: "round",
        interactive: false,
        bubblingMouseEvents: false,
      }).addTo(vesselTrackLayer);
      if (futureLatLngs.length > 1) {
        entry.futureLine = L.polyline(futureLatLngs, {
          color: entry.color,
          weight: 2,
          opacity: 0.34 * layerOpacity,
          dashArray: "4 10",
          lineCap: "round",
          interactive: false,
          bubblingMouseEvents: false,
        }).addTo(vesselTrackLayer);
      }
    } else {
      entry.trackLine = L.polyline(latLngs, {
        color: entry.color,
        weight: isActive ? 3 : 2,
        opacity: (isActive ? 0.92 : 0.22) * layerOpacity,
        dashArray: isActive ? "" : "4 10",
        lineCap: "round",
        interactive: false,
        bubblingMouseEvents: false,
      }).addTo(vesselTrackLayer);
    }
    entry.startMarker = L.circleMarker(latLngs[0], {
      radius: 4,
      color: entry.color,
      weight: 2,
      fillColor: "#ffffff",
      fillOpacity: (isActive ? 0.9 : 0.28) * layerOpacity,
      opacity: (isActive ? 1 : 0.35) * layerOpacity,
      interactive: false,
      bubblingMouseEvents: false,
    }).addTo(vesselTrackLayer);
    entry.endMarker = L.circleMarker(latLngs[latLngs.length - 1], {
      radius: 5,
      color: "#0f172a",
      weight: 2,
      fillColor: entry.color,
      fillOpacity: (isActive ? 0.95 : 0.35) * layerOpacity,
      opacity: (isActive ? 1 : 0.4) * layerOpacity,
      interactive: false,
      bubblingMouseEvents: false,
    }).addTo(vesselTrackLayer);
    if (expanded && points[cursor]) {
      const p = points[cursor];
      entry.playMarker = L.marker([p.lat, p.lon], {
        icon: makeVesselIcon(p.cogDeg, {
          fill: entry.color,
          border: "#ffffff",
        }),
        keyboard: false,
        interactive: false,
        zIndexOffset: 1200,
      }).addTo(vesselTrackLayer);
      entry.playMarker.setOpacity(layerOpacity);
    }
    return renderPoints;
  }

  function rerenderAllTrackedVesselGeometries() {
    trackedVessels.forEach((entry) => {
      if (!entry?.allPoints?.length) return;
      const renderPoints = renderTrackedVesselGeometry(entry);
      entry.renderedPoints = renderPoints.length;
    });
  }

  function reloadAllTrackedVessels() {
    trackedVessels.forEach((entry) => {
      if (!entry) return;
      loadVesselTrack(entry);
    });
  }

  function formatTrackDuration(startIso, endIso) {
    const a = Date.parse(String(startIso || ""));
    const b = Date.parse(String(endIso || ""));
    if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return "--";
    const mins = Math.max(0, Math.round((b - a) / 60000));
    if (mins < 120) return `${mins} min`;
    return `${(mins / 60).toFixed(1)} hr`;
  }

  function formatStepDateTime(isoLike) {
    const ms = Date.parse(String(isoLike || ""));
    if (!Number.isFinite(ms)) return "--";
    return new Date(ms).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  async function loadVesselTrack(entryOrMmsi, vesselName = "") {
    const entry = typeof entryOrMmsi === "string"
      ? createTrackedVesselEntry(entryOrMmsi, vesselName)
      : entryOrMmsi;
    if (!entry) return;
    const id = String(entry.mmsi || "").trim();
    if (!id) return;
    if (!activeTrackedVesselId) activeTrackedVesselId = id;
    const reqSeq = ++vesselTrackReqSeq;
    entry.reqSeq = reqSeq;
    entry.name = entry.name || String(vesselName || "");
    entry.hours = state.vesselTrackHours || 12;
    entry.loading = true;
    entry.error = "";
    entry.points = 0;
    entry.renderedPoints = 0;
    entry.startAt = "";
    entry.endAt = "";
    entry.distanceNm = 0;
    entry.avgSog = null;
    entry.maxSog = null;
    entry.allPoints = [];
    clearTrackedVesselGeometry(entry);
    render();
    try {
      const q = new URLSearchParams({
        mmsi: id,
        hours: String(state.vesselTrackHours || 12),
        limit: "20000",
      });
      const url = `${buildSameOriginUrl(AIS_DB_TRACKS_PATH) || AIS_DB_TRACKS_PATH}?${q.toString()}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json?.ok) throw new Error(String(json?.error || "track fetch failed"));
      const items = Array.isArray(json?.items) ? json.items : [];
      const points = items
        .map((row) => ({
          lat: Number(row?.lat),
          lon: Number(row?.lon),
          sogKn: Number.isFinite(Number(row?.sog_kn)) ? Number(row.sog_kn) : null,
          cogDeg: Number.isFinite(Number(row?.cog_deg)) ? Number(row.cog_deg) : null,
          receivedAt: String(row?.received_at || ""),
        }))
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
      if (entry.reqSeq !== reqSeq || !trackedVessels.has(id)) return;
      if (!points.length) {
        entry.loading = false;
        entry.error = "No historical points found for the selected window.";
        entry.points = 0;
        entry.renderedPoints = 0;
        render();
        return;
      }
      entry.allPoints = points;
      entry.playback.cursorIndex = Math.max(0, points.length - 1);
      entry.playback.lastTickMs = Date.now();
      entry.playback.carry = 0;
      const renderPoints = renderTrackedVesselGeometry(entry);
      let distanceNm = 0;
      for (let i = 1; i < points.length; i += 1) {
        distanceNm += haversineNm(points[i - 1].lat, points[i - 1].lon, points[i].lat, points[i].lon);
      }
      const sogVals = points.map((p) => p.sogKn).filter((n) => Number.isFinite(n));
      const avgSog = sogVals.length ? (sogVals.reduce((a, b) => a + b, 0) / sogVals.length) : null;
      const maxSog = sogVals.length ? Math.max(...sogVals) : null;
      const startAt = points[0]?.receivedAt || "";
      const endAt = points[points.length - 1]?.receivedAt || "";
      entry.loading = false;
      entry.error = "";
      entry.points = points.length;
      entry.renderedPoints = renderPoints.length;
      entry.startAt = startAt;
      entry.endAt = endAt;
      entry.distanceNm = distanceNm;
      entry.avgSog = avgSog;
      entry.maxSog = maxSog;
      const decimatedNote = renderPoints.length < points.length
        ? `, rendered ${renderPoints.length.toLocaleString()}`
        : "";
      setStatus(`Loaded vessel track: ${points.length.toLocaleString()} points${decimatedNote} (${id}).`, { log: true });
      render();
    } catch (err) {
      if (entry.reqSeq !== reqSeq || !trackedVessels.has(id)) return;
      entry.loading = false;
      entry.error = `Track load failed: ${err?.message || String(err)}`;
      setStatus(entry.error, { level: "error" });
      render();
    }
  }

  function getVesselTableRows(limit = 300) {
    const map = app.map.instance;
    const b = map.getBounds();
    const q = String(state.vesselFilterText || "").trim().toLowerCase();
    const rows = [];
    vesselMarkers.forEach((marker, id) => {
      const t = vesselLatest.get(id);
      const ll = marker?.getLatLng?.();
      const lat = Number.isFinite(Number(t?.lat)) ? Number(t.lat) : Number(ll?.lat);
      const lon = Number.isFinite(Number(t?.lon)) ? Number(t.lon) : Number(ll?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
      if (!b.contains([lat, lon])) return;
      const name = String(t?.name || "");
      if (q && !name.toLowerCase().includes(q)) return;
      if (!shouldDisplayLiveVessel(t)) return;
      const seen = Number(vesselSeenAt.get(id) || t?.updatedAt || 0);
      rows.push({
        id,
        name,
        lat,
        lon,
        sogKn: Number.isFinite(Number(t?.sogKn)) ? Number(t.sogKn) : null,
        cogDeg: Number.isFinite(Number(t?.cogDeg)) ? Number(t.cogDeg) : null,
        movementNm: Number.isFinite(Number(t?.movementNm)) ? Number(t.movementNm) : 0,
        seenAt: seen,
        colors: state.vesselColorMode === "movement"
          ? movementColor(t?.movementNm)
          : hashColor(id),
      });
    });
    rows.sort((a, b2) => b2.seenAt - a.seenAt);
    return rows.slice(0, limit);
  }

  function startAir() {
    pullState();
    if (airRunning) return;
    airRunning = true;
    pushLog("info", "Air stream started.");
    tickAir();
    airTimer = setInterval(tickAir, AIR_POLL_INTERVAL_MS);
    render();
  }

  function stopAir() {
    airRunning = false;
    if (airTimer) {
      clearInterval(airTimer);
      airTimer = null;
    }
    pushLog("info", "Air stream stopped.");
    render();
  }

  function startVessel() {
    pullState();
    if (vesselRunning) return;
    vesselRunning = true;
    vesselMsgCount = 0;
    vesselParsedCount = 0;
    vesselDroppedCount = 0;
    vesselPollFailCount = 0;
    pushLog("info", "Vessel stream started.", {
      scope: state.vesselScope,
      source: "db-api",
    });
    restoreCachedVessels();
    startVesselPolling();
    render();
  }

  function stopVessel() {
    vesselRunning = false;
    vesselMsgCount = 0;
    vesselParsedCount = 0;
    vesselDroppedCount = 0;
    vesselPollFailCount = 0;
    if (vesselPollTimer) {
      clearInterval(vesselPollTimer);
      vesselPollTimer = null;
    }
    if (vesselPollRetryTimer) {
      clearTimeout(vesselPollRetryTimer);
      vesselPollRetryTimer = null;
    }
    if (vesselCacheFlushTimer) {
      clearTimeout(vesselCacheFlushTimer);
      vesselCacheFlushTimer = null;
    }
    flushVesselCacheNow();
    clearVisibleVesselMarkers();
    pushLog("info", "Vessel stream stopped.");
    render();
  }

  function startVesselPolling() {
    if (!vesselRunning) return;
    if (vesselPollTimer) {
      clearInterval(vesselPollTimer);
      vesselPollTimer = null;
    }
    if (vesselPollRetryTimer) {
      clearTimeout(vesselPollRetryTimer);
      vesselPollRetryTimer = null;
    }
    vesselPollInFlight = false;
    pollVesselsOnce();
    vesselPollTimer = setInterval(pollVesselsOnce, VESSEL_POLL_INTERVAL_MS);
  }

  async function pollVesselsOnce() {
    if (!vesselRunning) return;
    if (vesselPollInFlight) return;
    vesselPollInFlight = true;
    const map = app.map.instance;
    const bounds = map.getBounds();
    const boxes = buildVesselBoundingBoxes(bounds);
    renderVesselQueryBboxes(boxes);
    const validBoxes = Array.isArray(boxes)
      ? boxes.filter((b) => Array.isArray(b) && Array.isArray(b[0]) && Array.isArray(b[1]))
      : [];
    if (!validBoxes.length) {
      vesselPollInFlight = false;
      return;
    }
    const displayCap = MAX_VESSEL_TARGETS;
    const fetchLimit = clamp(Math.max(displayCap * 3, 1200), displayCap, 5000);
    try {
      const targets = [];
      const seen = new Set();
      for (let bi = 0; bi < validBoxes.length; bi += 1) {
        const box = validBoxes[bi];
        const [sw, ne] = box;
        const bbox = `${sw[1]},${sw[0]},${ne[1]},${ne[0]}`;
        const qDb = new URLSearchParams({
          bbox,
          limit: String(fetchLimit),
          movement_min: String(clamp(Math.round(Number(state.vesselMoveWindowMin || 60)), 5, 360)),
        });
        const dbUrl = `${buildSameOriginUrl(AIS_DB_LATEST_PATH) || AIS_DB_LATEST_PATH}?${qDb.toString()}`;
        const dbRes = await fetch(dbUrl);
        if (!dbRes.ok) throw new Error(`HTTP ${dbRes.status}`);
        const dbJson = await dbRes.json();
        if (!dbJson?.ok) throw new Error(String(dbJson?.error || "latest failed"));
        const items = Array.isArray(dbJson?.items) ? dbJson.items : [];
        for (let i = 0; i < items.length; i += 1) {
          const row = items[i];
          const t = {
            id: String(row?.mmsi || ""),
            name: String(row?.vessel_name || ""),
            lat: Number(row?.lat),
            lon: Number(row?.lon),
            sogKn: Number.isFinite(Number(row?.sog_kn)) ? Number(row.sog_kn) : null,
            cogDeg: Number.isFinite(Number(row?.cog_deg)) ? Number(row.cog_deg) : null,
            movementNm: Number.isFinite(Number(row?.movement_nm)) ? Number(row.movement_nm) : 0,
          };
          if (!Number.isFinite(t.lat) || !Number.isFinite(t.lon) || !t.id) continue;
          if (seen.has(t.id)) continue;
          seen.add(t.id);
          targets.push(t);
        }
      }
      const cap = displayCap;
      const selectedTargets = selectStableVesselTargets(targets, cap);
      const throttledTargets = state.vesselScope === "map"
        ? selectedTargets
        : throttleNewVesselTargets(selectedTargets, cap);
      const now = Date.now();
      const latestBatchIds = new Set(throttledTargets.map((t) => String(t.id || "")));
      vesselPollFailCount = 0;
      vesselMsgCount += throttledTargets.length;
      for (let i = 0; i < throttledTargets.length; i += 1) {
        applyVesselTarget(throttledTargets[i]);
      }
      if (state.vesselScope === "map") {
        pruneVesselsOutsideBoxes(boxes);
      }
      pruneVessels(now);
      vesselCount = vesselMarkers.size;
      if (vesselCount > MAX_VESSEL_TARGETS) {
        trimOldestVessels(vesselCount - MAX_VESSEL_TARGETS, latestBatchIds);
        vesselCount = vesselMarkers.size;
      }
      setStatus(`AIS DB updated (${vesselCount.toLocaleString()} targets).`, { log: throttledTargets.length > 0 });
      updatePollStatusInplace();
    } catch (err) {
      vesselPollFailCount += 1;
      setStatus(`AIS DB poll failed: ${err?.message || String(err)}`, { level: "error" });
      if (!vesselPollRetryTimer) {
        vesselPollRetryTimer = setTimeout(() => {
          vesselPollRetryTimer = null;
          if (vesselRunning) pollVesselsOnce();
        }, 2000);
      }
      renderSafe();
    } finally {
      vesselPollInFlight = false;
    }
  }

  function applyVesselTarget(target, opts = {}) {
    if (!target || !Number.isFinite(Number(target.lat)) || !Number.isFinite(Number(target.lon))) return;
    const fromCache = !!opts.fromCache;
    const lat = Number(target.lat);
    const lon = Number(target.lon);
    const now = Date.now();
    const id = String(target.id || `${lat.toFixed(5)},${lon.toFixed(5)}`);
    if (!fromCache) vesselParsedCount += 1;
    vesselSeenAt.set(id, now);
    vesselLatest.set(id, {
      id,
      name: target.name || "",
      lat,
      lon,
      sogKn: Number.isFinite(Number(target.sogKn)) ? Number(target.sogKn) : null,
      cogDeg: Number.isFinite(Number(target.cogDeg)) ? Number(target.cogDeg) : null,
      movementNm: Number.isFinite(Number(target.movementNm)) ? Number(target.movementNm) : 0,
      updatedAt: now,
    });
    if (!shouldDisplayLiveVessel(target)) {
      const hiddenMarker = vesselMarkers.get(id);
      if (hiddenMarker) {
        vesselLayer.removeLayer(hiddenMarker);
        vesselMarkers.delete(id);
      }
      if (!fromCache) scheduleVesselCacheFlush();
      return;
    }
    const vesselColors = state.vesselColorMode === "movement"
      ? movementColor(target.movementNm)
      : hashColor(id);
    const layerOpacity = getVesselLayerOpacity();
    let marker = vesselMarkers.get(id);
    if (!marker) {
      marker = L.marker([lat, lon], {
        icon: makeVesselIcon(target.cogDeg, vesselColors),
        keyboard: false,
        riseOnHover: true,
      });
      marker.on("click", () => {
        toggleTrackedVessel(id, target.name || "");
      });
      marker.addTo(vesselLayer);
      marker.setOpacity(layerOpacity);
      vesselMarkers.set(id, marker);
    } else {
      marker.setLatLng([lat, lon]);
      marker.setOpacity(layerOpacity);
      if (!updateVesselIconElement(marker, target.cogDeg, vesselColors)) {
        marker.setIcon(makeVesselIcon(target.cogDeg, vesselColors));
      }
    }
    marker.setOpacity(layerOpacity);
    if (!updateVesselIconElement(marker, target.cogDeg, vesselColors)) {
      marker.setIcon(makeVesselIcon(target.cogDeg, vesselColors));
    }
    if (!fromCache) scheduleVesselCacheFlush();
  }

  function selectStableVesselTargets(targets, cap) {
    if (!Array.isArray(targets) || !targets.length) return [];
    const max = Math.max(1, Number(cap || 0));
    if (targets.length <= max) return targets;
    const byId = new Map(targets.map((t) => [String(t.id || ""), t]));
    const selected = [];
    const seen = new Set();
    const addById = (id) => {
      const key = String(id || "");
      if (!key || seen.has(key)) return;
      const t = byId.get(key);
      if (!t) return;
      seen.add(key);
      selected.push(t);
    };
    // Keep tracked vessels stable first.
    trackedVessels.forEach((_, id) => addById(id));
    // Then keep currently visible vessels stable if they still exist in this batch.
    vesselMarkers.forEach((_, id) => addById(id));
    // Fill remaining slots with new arrivals in backend order.
    for (let i = 0; i < targets.length && selected.length < max; i += 1) {
      addById(targets[i]?.id);
    }
    return selected.slice(0, max);
  }

  function throttleNewVesselTargets(targets, cap) {
    if (!Array.isArray(targets) || !targets.length) return [];
    const max = Math.max(1, Number(cap || 0));
    const existing = [];
    const newcomers = [];
    for (let i = 0; i < targets.length; i += 1) {
      const t = targets[i];
      const id = String(t?.id || "");
      if (!id) continue;
      if (vesselMarkers.has(id)) existing.push(t);
      else newcomers.push(t);
    }
    // Under heavy saturation, admit only a small number of new IDs per poll
    // so the on-map set remains visually stable instead of re-sampling every second.
    const freeSlots = Math.max(0, max - vesselMarkers.size);
    const churnBudget = Math.max(8, Math.min(40, Math.round(max * 0.015)));
    const allowNew = Math.min(newcomers.length, freeSlots + churnBudget);
    return existing.concat(newcomers.slice(0, allowNew));
  }

  function scheduleVesselCacheFlush() {
    if (vesselCacheFlushTimer) return;
    vesselCacheFlushTimer = setTimeout(() => {
      vesselCacheFlushTimer = null;
      flushVesselCacheNow();
    }, 2500);
  }

  function flushVesselCacheNow() {
    const items = Array.from(vesselLatest.values());
    items.sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0));
    saveVesselCache(vesselCacheKey, items.slice(0, MAX_VESSEL_CACHE));
  }

  function clearVisibleVesselMarkers() {
    vesselMarkers.forEach((marker, id) => {
      try { vesselLayer.removeLayer(marker); } catch {}
      vesselMarkers.delete(id);
      vesselSeenAt.delete(id);
      vesselLatest.delete(id);
    });
    vesselCount = vesselMarkers.size;
  }

  function restoreCachedVessels() {
    const cached = readVesselCache(vesselCacheKey);
    if (!cached.length) return;
    const boxes = buildVesselBoundingBoxes(app.map.instance.getBounds());
    const ttlMs = 45 * 60 * 1000;
    const now = Date.now();
    let restored = 0;
    for (let i = 0; i < cached.length; i += 1) {
      const t = cached[i];
      if ((now - Number(t.updatedAt || 0)) > ttlMs) continue;
      if (!pointInAnyBoundingBox(Number(t.lat), Number(t.lon), boxes)) continue;
      applyVesselTarget(t, { fromCache: true });
      restored += 1;
      if (restored >= Math.min(MAX_VESSEL_TARGETS, 700)) break;
    }
    if (restored > 0) {
      setStatus(`Restored ${restored.toLocaleString()} cached vessels, awaiting live updates.`, { level: "info" });
    }
  }

  function pointInAnyBoundingBox(lat, lon, boxes) {
    if (!Array.isArray(boxes) || !boxes.length) return true;
    for (let i = 0; i < boxes.length; i += 1) {
      const b = boxes[i];
      if (!Array.isArray(b) || b.length < 2 || !Array.isArray(b[0]) || !Array.isArray(b[1])) continue;
      const south = Number(b[0][0]); const west = Number(b[0][1]);
      const north = Number(b[1][0]); const east = Number(b[1][1]);
      if (lat >= south && lat <= north && lon >= west && lon <= east) return true;
    }
    return false;
  }

  function pruneVessels(nowMs) {
    const ttlMs = 15 * 60 * 1000;
    vesselMarkers.forEach((marker, id) => {
      const seen = vesselSeenAt.get(id) || 0;
      if ((nowMs - seen) <= ttlMs) return;
      vesselLayer.removeLayer(marker);
      vesselMarkers.delete(id);
      vesselSeenAt.delete(id);
    });
  }

  function pruneVesselsOutsideBoxes(boxes) {
    if (!Array.isArray(boxes) || !boxes.length) return;
    vesselMarkers.forEach((marker, id) => {
      const ll = marker?.getLatLng?.();
      const lat = Number(ll?.lat);
      const lon = Number(ll?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
      if (pointInAnyBoundingBox(lat, lon, boxes)) return;
      vesselLayer.removeLayer(marker);
      vesselMarkers.delete(id);
      vesselSeenAt.delete(id);
      vesselLatest.delete(id);
    });
  }

  function trimOldestVessels(removeCount, keepSet = null) {
    if (removeCount <= 0) return;
    const byAge = Array.from(vesselSeenAt.entries()).sort((a, b) => a[1] - b[1]);
    let removed = 0;
    for (let i = 0; i < byAge.length && removed < removeCount; i += 1) {
      const id = byAge[i][0];
      if (keepSet && keepSet.has(id)) continue;
      const marker = vesselMarkers.get(id);
      if (marker) vesselLayer.removeLayer(marker);
      vesselMarkers.delete(id);
      vesselSeenAt.delete(id);
      removed += 1;
    }
    for (let i = 0; i < byAge.length && removed < removeCount; i += 1) {
      const id = byAge[i][0];
      if (!vesselMarkers.has(id)) continue;
      const marker = vesselMarkers.get(id);
      if (marker) vesselLayer.removeLayer(marker);
      vesselMarkers.delete(id);
      vesselSeenAt.delete(id);
      removed += 1;
    }
  }

  function buildVesselBoundingBoxes(mapBounds) {
    if (state.vesselScope === "global") {
      return [[[-85, -180], [85, 180]]];
    }
    if (state.vesselScope === "map" && mapBounds) {
      const south = Number(mapBounds.getSouth());
      const north = Number(mapBounds.getNorth());
      let west = Number(mapBounds.getWest());
      let east = Number(mapBounds.getEast());
      if (![south, north, west, east].every((n) => Number.isFinite(n))) return [];
      west = Math.max(-180, Math.min(180, west));
      east = Math.max(-180, Math.min(180, east));
      if (west <= east) {
        return [[[south, west], [north, east]]];
      }
      // Map viewport crosses antimeridian, split into two query boxes.
      return [
        [[south, west], [north, 180]],
        [[south, -180], [north, east]],
      ];
    }
    // Default: broad North America maritime region for reliable visibility.
    return [[[24, -126], [50, -66]]];
  }

  function render() {
    if (!containerEl) return;
    containerEl.querySelectorAll("details[data-lt-section]").forEach((el) => {
      const key = String(el.getAttribute("data-lt-section") || "");
      if (!key) return;
      state.sectionOpen = state.sectionOpen || {};
      state.sectionOpen[key] = !!el.open;
    });
    saveState(stateKey, state);
    const prevFilterEl = containerEl.querySelector("#lt-vessel-filter");
    const prevFilterValue = prevFilterEl ? String(prevFilterEl.value || "") : String(state.vesselFilterText || "");
    const prevFilterFocused = !!prevFilterEl && document.activeElement === prevFilterEl;
    const prevFilterSelStart = prevFilterFocused ? Number(prevFilterEl.selectionStart ?? prevFilterValue.length) : null;
    const prevFilterSelEnd = prevFilterFocused ? Number(prevFilterEl.selectionEnd ?? prevFilterValue.length) : null;
    if (prevFilterFocused) {
      state.vesselFilterText = prevFilterValue;
    }
    containerEl.innerHTML = `
      <details class="toolset-group" data-lt-section="controls" ${state.sectionOpen?.controls === true ? "open" : ""}>
        <summary>${airMode ? "FAA Air Traffic" : "AIS Vessel Traffic"}</summary>
        <div class="toolset-content">
        <div class="small" style="margin:6px 0 10px;">
          ${airMode
    ? "Realtime air traffic via OpenSky polling with ADSB fallback."
    : "Realtime vessel traffic via local AIS database API."}
        </div>

        ${airMode ? `
          <div class="row lm-add-row" style="margin-top:6px;">
            <label class="small" for="lt-opensky-user" style="min-width:88px;">OpenSky user</label>
            <input id="lt-opensky-user" type="text" value="${esc(state.openskyUsername)}" style="flex:1;" />
          </div>
          <div class="row lm-add-row" style="margin-top:6px;">
            <label class="small" for="lt-opensky-pass" style="min-width:88px;">OpenSky pass</label>
            <input id="lt-opensky-pass" type="password" value="${esc(state.openskyPassword)}" style="flex:1;" />
          </div>
          <div class="row lm-add-row" style="margin-top:6px;">
            <label class="small" for="lt-track-on-click" style="min-width:88px;">Track by click</label>
            <input id="lt-track-on-click" type="checkbox" ${state.trackOnClick ? "checked" : ""} />
            <div class="small" style="margin-left:8px;">Clicking an aircraft toggles tracked state.</div>
          </div>
        ` : `
          <div class="row lm-add-row" style="margin-top:6px; align-items:center; gap:8px;">
            <input id="lt-vessel-live" type="checkbox" ${vesselRunning ? "checked" : ""} />
            <label class="small" for="lt-vessel-live">Live Vessels</label>
          </div>
          <div class="row lm-add-row" style="margin-top:6px; flex-direction:column; align-items:stretch; gap:4px;">
            <label class="small" for="lt-vessel-opacity">Layer opacity (<span data-lt-vessel-opacity-label>${Math.round(getVesselLayerOpacity() * 100)}%</span>)</label>
            <div
              data-lt-vessel-opacitybar
              data-lt-vessel-opacity-value="${Math.round(getVesselLayerOpacity() * 100)}"
              style="position:relative; height:18px; cursor:pointer; user-select:none; touch-action:none;"
            >
              <div style="position:absolute; left:0; right:0; top:50%; height:4px; transform:translateY(-50%); border-radius:999px; background:rgba(31,41,55,1);"></div>
              <div data-lt-vessel-opacity-fill style="position:absolute; left:0; top:50%; height:4px; transform:translateY(-50%); border-radius:999px; width:${Math.round(getVesselLayerOpacity() * 100)}%; background:rgba(56,189,248,.95);"></div>
              <div data-lt-vessel-opacity-handle style="position:absolute; top:50%; left:${Math.round(getVesselLayerOpacity() * 100)}%; width:14px; height:14px; border-radius:999px; transform:translate(-50%, -50%); background:rgba(56,189,248,1); box-shadow:0 0 0 4px rgba(56,189,248,.18); border:1px solid rgba(15,23,42,1);"></div>
            </div>
          </div>
          <div class="row lm-add-row" style="margin-top:6px; flex-direction:column; align-items:stretch; gap:4px;">
            <label class="small" for="lt-vessel-activity-filter">Vessel visibility</label>
            <select id="lt-vessel-activity-filter" style="width:100%;">
              <option value="all" ${state.vesselActivityFilter !== "active" ? "selected" : ""}>All Vessels</option>
              <option value="active" ${state.vesselActivityFilter === "active" ? "selected" : ""}>Active Only</option>
            </select>
          </div>
          <div class="row lm-add-row" style="margin-top:6px; flex-direction:column; align-items:stretch; gap:4px;">
            <label class="small" for="lt-vessel-color-mode">Color mode</label>
            <select id="lt-vessel-color-mode" style="width:100%;">
              <option value="identity" ${state.vesselColorMode !== "movement" ? "selected" : ""}>Identity</option>
              <option value="movement" ${state.vesselColorMode === "movement" ? "selected" : ""}>Movement</option>
            </select>
          </div>
          <div class="row lm-add-row" style="margin-top:6px; flex-direction:column; align-items:stretch; gap:4px;">
            <label class="small" for="lt-vessel-move-window">Movement window</label>
            <select id="lt-vessel-move-window" style="width:100%;" ${state.vesselColorMode === "movement" ? "" : "disabled"}>
              <option value="15" ${Number(state.vesselMoveWindowMin) === 15 ? "selected" : ""}>15 min</option>
              <option value="30" ${Number(state.vesselMoveWindowMin) === 30 ? "selected" : ""}>30 min</option>
              <option value="60" ${Number(state.vesselMoveWindowMin) === 60 ? "selected" : ""}>60 min</option>
              <option value="120" ${Number(state.vesselMoveWindowMin) === 120 ? "selected" : ""}>120 min</option>
              <option value="240" ${Number(state.vesselMoveWindowMin) === 240 ? "selected" : ""}>240 min</option>
              <option value="360" ${Number(state.vesselMoveWindowMin) === 360 ? "selected" : ""}>360 min</option>
            </select>
          </div>
          ${state.vesselColorMode === "movement" ? `
          <div class="small" style="margin-top:6px;">
            Movement legend (${Number(state.vesselMoveWindowMin || 60)} min): 
            <span style="color:hsla(210, 14%, 64%, 1);">0-0.1 nm</span>,
            <span style="color:hsla(145, 58%, 68%, 1);">0.1-1 nm</span>,
            <span style="color:hsla(36, 85%, 66%, 1);">1-5 nm</span>,
            <span style="color:hsla(5, 78%, 66%, 1);">5+ nm</span>
          </div>
          ` : ""}
          <div class="row lm-add-row" style="margin-top:6px; flex-direction:column; align-items:stretch; gap:4px;">
            <label class="small" for="lt-vessel-scope">Vessel scope</label>
            <select id="lt-vessel-scope" style="width:100%;">
              <option value="regional-us" ${state.vesselScope === "regional-us" ? "selected" : ""}>US coastal region</option>
              <option value="map" ${state.vesselScope === "map" ? "selected" : ""}>Current map bounds</option>
              <option value="global" ${state.vesselScope === "global" ? "selected" : ""}>Global</option>
            </select>
          </div>
          <div class="row lm-add-row" style="margin-top:6px; flex-direction:column; align-items:stretch; gap:4px;">
            <label class="small" for="lt-vessel-track-hours">Track window</label>
            <select id="lt-vessel-track-hours" style="width:100%;">
              <option value="1" ${Number(state.vesselTrackHours) === 1 ? "selected" : ""}>1h</option>
              <option value="3" ${Number(state.vesselTrackHours) === 3 ? "selected" : ""}>3h</option>
              <option value="6" ${Number(state.vesselTrackHours) === 6 ? "selected" : ""}>6h</option>
              <option value="12" ${Number(state.vesselTrackHours) === 12 ? "selected" : ""}>12h</option>
              <option value="24" ${Number(state.vesselTrackHours) === 24 ? "selected" : ""}>24h</option>
              <option value="48" ${Number(state.vesselTrackHours) === 48 ? "selected" : ""}>48h</option>
            </select>
          </div>
          ${(() => {
            const tone = getAisHealthTone(aisHealth.status);
            return `
              <div style="margin-top:8px; padding:8px 10px; border:1px solid ${tone.border}; border-radius:10px; background:${tone.accent};">
                <div class="row" style="justify-content:space-between; align-items:center; gap:8px;">
                  <div class="small" style="font-weight:600;">AIS Backend Health</div>
                  <div class="small" style="display:inline-flex; align-items:center; gap:6px;">
                    <span style="width:8px; height:8px; border-radius:999px; background:${tone.color}; box-shadow:0 0 0 3px ${tone.accent};"></span>
                    <strong>${tone.label}</strong>
                  </div>
                </div>
                <div class="small" style="margin-top:6px;">
                  Last ingest: <strong>${esc(aisHealth.lastReceivedAt ? formatRelativeTimeFromIso(aisHealth.lastReceivedAt) : "--")}</strong>,
                  lag: <strong>${esc(Number.isFinite(aisHealth.lagSec) ? `${aisHealth.lagSec.toFixed(1)}s` : "--")}</strong>,
                  live vessels: <strong>${esc(formatCompactCount(aisHealth.latestCount))}</strong>,
                  pts/hr: <strong>${esc(formatCompactCount(aisHealth.pointsLastHour))}</strong>
                </div>
                ${aisHealth.error ? `<div class="small" style="margin-top:6px; color:#fecaca;">${esc(aisHealth.error)}</div>` : ""}
                ${renderAisContinuityBar()}
                <div class="small" style="margin-top:6px;">Updated ${esc(aisHealth.updatedAt ? formatAge(Date.now() - aisHealth.updatedAt) : "--")} ago from ${esc(getAisDbApiBase())}.</div>
              </div>
            `;
          })()}
          <div class="small" style="margin-top:6px;">AIS source is proxied through the current dashboard origin at <code>/api/ais</code> (${esc(getAisDbApiBase())}).</div>
        `}

        ${airMode ? `
        <div class="row lm-add-row" style="margin-top:8px;">
          <button id="lt-air-toggle" class="btn ${airRunning ? "" : "primary"}">${airRunning ? "Stop Air" : "Start Air"}</button>
          <button id="lt-refresh-now" class="btn" ${airLoading ? "disabled" : ""}>Refresh Air Now</button>
        </div>` : ""}

        ${airMode
    ? `<div class="small" style="margin-top:10px;">
             Air status: <strong>${airRunning ? (airLoading ? "updating..." : "running") : "stopped"}</strong>,
             targets: <strong>${airCount.toLocaleString()}</strong>
           </div>`
    : `<div class="small" style="margin-top:4px;" data-lt-vessel-info>
             Vessel status: <strong>${vesselRunning ? (vesselPollInFlight ? "updating..." : "running") : "stopped"}</strong>,
             source: <strong>local db api</strong>,
             targets: <strong>${vesselCount.toLocaleString()}</strong>
           </div>`}
        <div class="small" style="margin-top:6px; min-height:16px;" data-lt-status>${esc(status)}</div>
        </div>
      </details>
      ${vesselMode ? `
      <details class="toolset-group" data-lt-section="vesselTable" ${state.sectionOpen?.vesselTable === true ? "open" : ""} style="margin-top:8px;">
        <summary>Vessel Table (Current View)</summary>
        <div class="toolset-content">
        <div class="small" style="margin-top:4px;">Click a vessel row to zoom to it.</div>
        <div class="row" style="margin-top:6px;">
          <label class="small" for="lt-vessel-filter" style="min-width:62px;">Filter</label>
          <input id="lt-vessel-filter" type="text" value="${esc(prevFilterFocused ? prevFilterValue : (state.vesselFilterText || ""))}" placeholder="Type vessel name..." style="flex:1;" />
        </div>
        <div style="margin-top:6px; max-height:220px; overflow:auto; border:1px solid rgba(148,163,184,.22); border-radius:8px;">
          <table style="width:100%; border-collapse:collapse; font-size:11px;">
            <thead>
              <tr style="position:sticky; top:0; background:rgba(2,6,23,.95);">
                <th style="text-align:left; padding:6px; border-bottom:1px solid rgba(148,163,184,.2);">MMSI</th>
                <th style="text-align:left; padding:6px; border-bottom:1px solid rgba(148,163,184,.2);">Name</th>
                <th style="text-align:right; padding:6px; border-bottom:1px solid rgba(148,163,184,.2);">Lat</th>
                <th style="text-align:right; padding:6px; border-bottom:1px solid rgba(148,163,184,.2);">Lon</th>
                <th style="text-align:right; padding:6px; border-bottom:1px solid rgba(148,163,184,.2);">SOG</th>
                <th style="text-align:right; padding:6px; border-bottom:1px solid rgba(148,163,184,.2);">COG</th>
                <th style="text-align:right; padding:6px; border-bottom:1px solid rgba(148,163,184,.2);">Move</th>
                <th style="text-align:right; padding:6px; border-bottom:1px solid rgba(148,163,184,.2);">Seen</th>
              </tr>
            </thead>
            <tbody>
              ${(() => {
                const rows = getVesselTableRows(350);
                if (!rows.length) {
                  return `<tr><td colspan="8" class="small" style="padding:8px;">No vessels match current map view/filter.</td></tr>`;
                }
                const now = Date.now();
                return rows.map((r) => {
                  const ageMs = now - Number(r.seenAt || 0);
                  return `
                    <tr data-lt-vessel-zoom="${esc(r.id)}" style="cursor:pointer; border-bottom:1px solid rgba(148,163,184,.12); background:${esc(r.colors?.accent || "transparent")};">
                      <td style="padding:6px; border-left:3px solid ${esc(r.colors?.border || "rgba(148,163,184,.5)")};">
                        <span style="display:inline-block; width:8px; height:8px; border-radius:999px; background:${esc(r.colors?.fill || "rgba(148,163,184,.35)")}; border:1px solid ${esc(r.colors?.border || "rgba(148,163,184,.5)")}; margin-right:6px; vertical-align:middle;"></span>
                        ${esc(r.id)}
                      </td>
                      <td style="padding:6px;">${esc(r.name || "--")}</td>
                      <td style="padding:6px; text-align:right;">${r.lat.toFixed(5)}</td>
                      <td style="padding:6px; text-align:right;">${r.lon.toFixed(5)}</td>
                      <td style="padding:6px; text-align:right;">${toFixed(r.sogKn, 1)}</td>
                      <td style="padding:6px; text-align:right;">${toFixed(r.cogDeg, 0)}</td>
                      <td style="padding:6px; text-align:right;">${toFixed(r.movementNm, 2)} nm</td>
                      <td style="padding:6px; text-align:right;">${formatAge(ageMs)}</td>
                    </tr>
                  `;
                }).join("");
              })()}
            </tbody>
          </table>
        </div>
        </div>
      </details>
      <details class="toolset-group" data-lt-section="trackedVessels" ${state.sectionOpen?.trackedVessels === true ? "open" : ""} style="margin-top:8px;">
        <summary>Tracked Vessels (${trackedVessels.size})</summary>
        <div class="toolset-content">
        <div class="row" style="margin-top:6px; flex-direction:column; align-items:stretch; gap:6px;">
          <label class="small" for="lt-vessel-track-mode">Track detail</label>
          <select id="lt-vessel-track-mode" style="width:100%;">
            <option value="decimated" ${state.vesselTrackRenderMode !== "full" ? "selected" : ""}>Decimated</option>
            <option value="full" ${state.vesselTrackRenderMode === "full" ? "selected" : ""}>Full</option>
          </select>
          <div class="row" style="gap:6px;">
            <button id="lt-track-zoom" class="btn" type="button" ${trackedVessels.size ? "" : "disabled"}>Zoom Tracks</button>
            <button id="lt-track-clear" class="btn" type="button" ${trackedVessels.size ? "" : "disabled"}>Clear All</button>
          </div>
        </div>
        ${(() => {
          const rows = Array.from(trackedVessels.values())
            .sort((a, b) => Number(b.trackedAt || 0) - Number(a.trackedAt || 0));
          if (!rows.length) {
            return `<div class="small" style="margin-top:6px;">Click vessel markers to toggle tracked vessel history. Metrics are shown here.</div>`;
          }
          return `<div style="margin-top:6px; display:grid; gap:6px;">
            ${rows.map((tv) => `
              <div class="row" style="justify-content:space-between; gap:8px; border:1px solid ${activeTrackedVesselId === tv.mmsi ? esc(tv.color) : "rgba(148,163,184,.25)"}; border-radius:8px; padding:6px 8px; background:${activeTrackedVesselId === tv.mmsi ? "rgba(15,23,42,.55)" : "rgba(2,6,23,.25)"};">
                <button class="btn" type="button" data-lt-vtrack-toggle="${esc(tv.mmsi)}" style="display:flex; align-items:center; gap:7px; flex:1; justify-content:flex-start;">
                  <span style="width:10px; height:10px; border-radius:999px; background:${esc(tv.color)}; box-shadow:0 0 6px ${esc(tv.color)};"></span>
                  <span>${esc(tv.name || tv.mmsi)}</span>
                  <span class="small">(MMSI ${esc(tv.mmsi)})</span>
                  <span class="small">${activeTrackedVesselId === tv.mmsi ? "ACTIVE - " : ""}${tv.loading ? "loading..." : `pts ${Number(tv.renderedPoints || tv.points || 0).toLocaleString()}/${Number(tv.points || 0).toLocaleString()} - ${formatTrackDuration(tv.startAt, tv.endAt)} - ${toFixed(tv.distanceNm, 2)} nm - avg ${toFixed(tv.avgSog, 1)} kt - max ${toFixed(tv.maxSog, 1)} kt`}</span>
                </button>
                <button class="btn" type="button" title="Zoom vessel" aria-label="Zoom vessel" data-lt-vtrack-zoom="${esc(tv.mmsi)}">+</button>
                <button class="btn" type="button" title="Reload track" aria-label="Reload track" data-lt-vtrack-refresh="${esc(tv.mmsi)}">&#x21BB;</button>
                <button class="btn" type="button" title="Remove tracked vessel" aria-label="Remove tracked vessel" data-lt-vtrack-untrack="${esc(tv.mmsi)}">x</button>
              </div>
              ${expandedTrackedVesselId === tv.mmsi ? (() => {
                const maxIdx = Math.max(0, Number(tv.points || 0) - 1);
                const cursorIdx = clamp(Math.round(Number(tv.playback?.cursorIndex || 0)), 0, maxIdx);
                const cursorTs = tv.allPoints?.[cursorIdx]?.receivedAt || tv.endAt || "";
                const cursorText = formatStepDateTime(cursorTs);
                return `
                  <div style="margin:4px 2px 0 2px; padding:8px; border:1px solid rgba(148,163,184,.2); border-radius:8px; background:rgba(2,6,23,.42);">
                    <div class="row" style="gap:6px; align-items:center;">
                      <button class="btn" type="button" title="${tv.playback?.isPlaying ? "Pause" : "Play"}" aria-label="${tv.playback?.isPlaying ? "Pause" : "Play"}" data-lt-vtrack-play="${esc(tv.mmsi)}">${tv.playback?.isPlaying ? "&#x23F8;" : "&#x25B6;"}</button>
                      <select id="lt-vtrack-speed-${esc(tv.mmsi)}" data-lt-vtrack-speed="${esc(tv.mmsi)}" style="width:78px;">
                        <option value="0.5" ${Number(tv.playback?.speed || 1) === 0.5 ? "selected" : ""}>0.5x</option>
                        <option value="1" ${Number(tv.playback?.speed || 1) === 1 ? "selected" : ""}>1x</option>
                        <option value="2" ${Number(tv.playback?.speed || 1) === 2 ? "selected" : ""}>2x</option>
                        <option value="4" ${Number(tv.playback?.speed || 1) === 4 ? "selected" : ""}>4x</option>
                      </select>
                      <div class="small" data-lt-vtrack-point="${esc(tv.mmsi)}">Point ${Number(cursorIdx + 1).toLocaleString()} / ${Number(maxIdx + 1).toLocaleString()}</div>
                      <div class="small" data-lt-vtrack-time="${esc(tv.mmsi)}">Current: ${esc(cursorText)}</div>
                    </div>
                    <div class="row" style="margin-top:8px; gap:8px; align-items:center;">
                      <div
                        data-lt-vtrack-scrubbar="${esc(tv.mmsi)}"
                        data-lt-vtrack-max="${maxIdx}"
                        data-lt-vtrack-value="${cursorIdx}"
                        style="position:relative; flex:1; height:18px; cursor:pointer; user-select:none; touch-action:none;"
                      >
                        <div style="position:absolute; left:0; right:0; top:50%; height:4px; transform:translateY(-50%); border-radius:999px; background:rgba(31,41,55,1);"></div>
                        <div data-lt-vtrack-scrubfill="${esc(tv.mmsi)}" style="position:absolute; left:0; top:50%; height:4px; transform:translateY(-50%); border-radius:999px; width:${maxIdx > 0 ? ((cursorIdx / maxIdx) * 100).toFixed(2) : "0"}%; background:${esc(tv.color)};"></div>
                        <div data-lt-vtrack-scrubhandle="${esc(tv.mmsi)}" style="position:absolute; top:50%; left:${maxIdx > 0 ? ((cursorIdx / maxIdx) * 100).toFixed(2) : "0"}%; width:14px; height:14px; border-radius:999px; transform:translate(-50%, -50%); background:${esc(tv.color)}; box-shadow:0 0 0 4px rgba(56,189,248,.18); border:1px solid rgba(15,23,42,1);"></div>
                      </div>
                    </div>
                  </div>
                `;
              })() : ""}
              ${tv.error ? `<div class="small" style="color:#fca5a5; margin-left:2px;">${esc(tv.error)}</div>` : ""}
            `).join("")}
          </div>`;
        })()}
        </div>
      </details>
      ` : ""}
      ${airMode ? `
      <details class="toolset-group" data-lt-section="trackedFlights" ${state.sectionOpen?.trackedFlights === true ? "open" : ""} style="margin-top:8px;">
        <summary>Tracked Flights (${trackedFlights.size})</summary>
        <div class="toolset-content">
        <div style="margin-top:6px; display:grid; gap:6px;">
          ${trackedFlights.size ? Array.from(trackedFlights.values()).map((t) => {
            const speedKn = Number.isFinite(t.velocityMs) ? t.velocityMs * 1.94384 : null;
            const altFt = Number.isFinite(t.altitudeM) ? t.altitudeM * 3.28084 : null;
            return `
              <div class="row" style="justify-content:space-between; gap:8px; border:1px solid rgba(148,163,184,.25); border-radius:8px; padding:6px 8px;">
                <button class="btn" type="button" data-lt-zoom="${esc(t.id)}" style="display:flex; align-items:center; gap:7px; flex:1; justify-content:flex-start;">
                  <span style="width:10px; height:10px; border-radius:999px; background:${esc(t.color)}; box-shadow:0 0 6px ${esc(t.color)};"></span>
                  <span>${esc(t.callsign || t.id)}</span>
                  <span class="small">(${esc(t.country || "--")})</span>
                  <span class="small">spd ${toFixed(speedKn, 0)} kt · alt ${toFixed(altFt, 0)} ft</span>
                </button>
                <button class="btn" type="button" data-lt-untrack="${esc(t.id)}">Untrack</button>
              </div>
            `;
          }).join("") : `<div class="small">No tracked flights. Enable <strong>Track by click</strong> and click aircraft on the map.</div>`}
        </div>
        </div>
      </details>
      ` : ""}
      <details class="toolset-group" data-lt-section="diagnostics" ${state.sectionOpen?.diagnostics === true ? "open" : ""} style="margin-top:8px;">
        <summary>Diagnostics Log (${diagLog.length})</summary>
        <div class="toolset-content">
        <div class="row" style="justify-content:space-between; align-items:center;">
          <div class="small">Export controls</div>
          <div class="row" style="gap:6px;">
            <label class="btn" style="display:inline-flex; align-items:center; gap:6px;">
              <input id="lt-log-live" type="checkbox" ${diagLiveFollow ? "checked" : ""} />
              Live
            </label>
            <button id="lt-log-copy" class="btn" type="button">Copy</button>
            <button id="lt-log-download" class="btn" type="button">Download</button>
            <button id="lt-log-clear" class="btn" type="button">Clear</button>
          </div>
        </div>
        <textarea id="lt-log-text" readonly style="margin-top:6px; width:100%; height:160px; resize:vertical; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size:11px;">${esc(formatLog())}</textarea>
        <div class="small" style="margin-top:6px;">Use Copy/Download to send exact diagnostics without retyping.</div>
        </div>
      </details>
    `;

    const airBtn = containerEl.querySelector("#lt-air-toggle");
    const refreshBtn = containerEl.querySelector("#lt-refresh-now");
    const diagTextEl = containerEl.querySelector("#lt-log-text");
    const diagLiveEl = containerEl.querySelector("#lt-log-live");

    if (diagTextEl) {
      if (diagLiveFollow) diagTextEl.scrollTop = diagTextEl.scrollHeight;
      else diagTextEl.scrollTop = Math.max(0, Math.min(diagScrollTop, diagTextEl.scrollHeight));
      diagTextEl.addEventListener("scroll", () => {
        if (!diagLiveFollow) diagScrollTop = diagTextEl.scrollTop;
      });
    }
    diagLiveEl?.addEventListener("change", () => {
      diagLiveFollow = !!diagLiveEl.checked;
      if (diagLiveFollow && diagTextEl) diagTextEl.scrollTop = diagTextEl.scrollHeight;
      if (!diagLiveFollow && diagTextEl) diagScrollTop = diagTextEl.scrollTop;
    });

    airBtn?.addEventListener("click", () => {
      pullState();
      if (airRunning) stopAir();
      else startAir();
    });
    refreshBtn?.addEventListener("click", () => {
      pullState();
      tickAir();
    });
    containerEl.querySelector("#lt-log-copy")?.addEventListener("click", async () => {
      const txt = formatLog();
      const ok = await copyLogText(txt);
      setStatus(ok ? "Diagnostics copied to clipboard." : "Copy failed. Use Download.", { level: ok ? "info" : "warn", log: true });
      render();
    });
    containerEl.querySelector("#lt-log-download")?.addEventListener("click", () => {
      downloadLogText(formatLog());
      setStatus("Diagnostics downloaded to file.", { level: "info", log: true });
      render();
    });
    containerEl.querySelector("#lt-log-clear")?.addEventListener("click", () => {
      diagLog = [];
      saveLog(logKey, diagLog);
      setStatus("Diagnostics log cleared.", { level: "info", log: true });
      render();
    });
    containerEl.querySelectorAll("[data-lt-zoom]").forEach((btn) => {
      btn.addEventListener("click", () => zoomToTrackedFlight(btn.getAttribute("data-lt-zoom")));
    });
    containerEl.querySelectorAll("[data-lt-untrack]").forEach((btn) => {
      btn.addEventListener("click", () => toggleTrackedFlight(btn.getAttribute("data-lt-untrack")));
    });
    containerEl.querySelectorAll("[data-lt-vessel-zoom]").forEach((row) => {
      row.addEventListener("click", () => {
        const id = row.getAttribute("data-lt-vessel-zoom");
        if (trackedVessels.has(String(id || ""))) {
          setActiveTrackedVessel(id);
        }
        zoomToVessel(id);
        render();
      });
    });
    containerEl.querySelectorAll("[data-lt-vtrack-toggle]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.getAttribute("data-lt-vtrack-toggle");
        toggleTrackedVesselExpanded(id);
      });
    });
    containerEl.querySelectorAll("[data-lt-vtrack-zoom]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.getAttribute("data-lt-vtrack-zoom");
        setActiveTrackedVessel(id);
        zoomToVessel(id);
        render();
      });
    });
    containerEl.querySelectorAll("[data-lt-vtrack-refresh]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = String(btn.getAttribute("data-lt-vtrack-refresh") || "");
        setActiveTrackedVessel(id);
        const entry = trackedVessels.get(id);
        if (entry) loadVesselTrack(entry);
      });
    });
    containerEl.querySelectorAll("[data-lt-vtrack-untrack]").forEach((btn) => {
      btn.addEventListener("click", () => {
        untrackVessel(btn.getAttribute("data-lt-vtrack-untrack"));
        render();
      });
    });
    containerEl.querySelectorAll("[data-lt-vtrack-play]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.getAttribute("data-lt-vtrack-play");
        setActiveTrackedVessel(id);
        toggleTrackedVesselPlayback(id);
      });
    });
    containerEl.querySelectorAll("[data-lt-vtrack-speed]").forEach((el) => {
      el.addEventListener("change", () => {
        const id = el.getAttribute("data-lt-vtrack-speed");
        setActiveTrackedVessel(id);
        setTrackedVesselPlaybackSpeed(id, el.value);
        render();
      });
    });
    containerEl.querySelectorAll("[data-lt-vtrack-scrubbar]").forEach((bar) => {
      const id = String(bar.getAttribute("data-lt-vtrack-scrubbar") || "");
      const maxIdx = Math.max(0, Math.round(Number(bar.getAttribute("data-lt-vtrack-max") || 0)));
      const fillEl = bar.querySelector("[data-lt-vtrack-scrubfill]");
      const handleEl = bar.querySelector("[data-lt-vtrack-scrubhandle]");
      const pointEl = containerEl.querySelector(`[data-lt-vtrack-point="${id}"]`);
      const timeEl = containerEl.querySelector(`[data-lt-vtrack-time="${id}"]`);
      let dragging = false;

      const clampPct = (pct) => Math.max(0, Math.min(100, pct));
      const valueToPct = (v) => (maxIdx > 0 ? clampPct((Number(v) / maxIdx) * 100) : 0);
      const updateVisual = (v) => {
        const pct = valueToPct(v);
        if (fillEl) fillEl.style.width = `${pct}%`;
        if (handleEl) handleEl.style.left = `${pct}%`;
        bar.setAttribute("data-lt-vtrack-value", String(Math.round(Number(v) || 0)));
        if (pointEl) {
          pointEl.textContent = `Point ${Number(Math.round(Number(v) + 1)).toLocaleString()} / ${Number(maxIdx + 1).toLocaleString()}`;
        }
        if (timeEl) {
          const entry = trackedVessels.get(id);
          const ts = entry?.allPoints?.[Math.round(Number(v) || 0)]?.receivedAt || entry?.endAt || "";
          timeEl.textContent = `Current: ${formatStepDateTime(ts)}`;
        }
      };
      const eventClientX = (ev) => {
        if (Number.isFinite(ev?.clientX)) return Number(ev.clientX);
        const t = ev?.touches?.[0] || ev?.changedTouches?.[0];
        return Number(t?.clientX);
      };
      const xToValue = (clientX) => {
        const r = bar.getBoundingClientRect();
        if (!Number.isFinite(clientX) || r.width <= 0 || maxIdx <= 0) return 0;
        const pct = clampPct(((clientX - r.left) / r.width) * 100);
        return clamp(Math.round((pct / 100) * maxIdx), 0, maxIdx);
      };
      const commit = (v) => {
        setTrackedVesselPlaybackCursor(id, v, { renderPanel: true, renderGeometry: true });
      };
      const move = (ev) => {
        if (!dragging) return;
        const v = xToValue(eventClientX(ev));
        updateVisual(v);
        // Live scrub: update vessel geometry on every drag movement.
        setTrackedVesselPlaybackCursor(id, v, { renderPanel: false, renderGeometry: true });
      };
      const end = (ev) => {
        if (!dragging) return;
        dragging = false;
        isScrubbingVesselTrack = false;
        const v = xToValue(eventClientX(ev));
        updateVisual(v);
        commit(v);
        document.removeEventListener("pointermove", move);
        document.removeEventListener("pointerup", end);
        document.removeEventListener("pointercancel", end);
      };
      const begin = (ev) => {
        ev.preventDefault();
        dragging = true;
        isScrubbingVesselTrack = true;
        const v = xToValue(eventClientX(ev));
        updateVisual(v);
        setTrackedVesselPlaybackCursor(id, v, { renderPanel: false, renderGeometry: true });
        document.addEventListener("pointermove", move);
        document.addEventListener("pointerup", end);
        document.addEventListener("pointercancel", end);
      };

      bar.addEventListener("pointerdown", begin);
      bar.addEventListener("mousedown", begin);
      bar.addEventListener("touchstart", begin, { passive: false });
    });
    containerEl.querySelector("#lt-track-zoom")?.addEventListener("click", () => {
      zoomToTrackedVesselTracks();
    });
    containerEl.querySelector("#lt-track-clear")?.addEventListener("click", () => {
      clearAllTrackedVessels();
      render();
    });
    const vesselFilterEl = containerEl.querySelector("#lt-vessel-filter");
    const vesselLiveEl = containerEl.querySelector("#lt-vessel-live");
    const vesselOpacityLabelEl = containerEl.querySelector("[data-lt-vessel-opacity-label]");
    const vesselOpacityBarEl = containerEl.querySelector("[data-lt-vessel-opacitybar]");
    const vesselOpacityFillEl = containerEl.querySelector("[data-lt-vessel-opacity-fill]");
    const vesselOpacityHandleEl = containerEl.querySelector("[data-lt-vessel-opacity-handle]");
    const vesselActivityFilterEl = containerEl.querySelector("#lt-vessel-activity-filter");
    const vesselScopeEl = containerEl.querySelector("#lt-vessel-scope");
    const vesselColorModeEl = containerEl.querySelector("#lt-vessel-color-mode");
    const vesselMoveWindowEl = containerEl.querySelector("#lt-vessel-move-window");
    vesselLiveEl?.addEventListener("change", () => {
      const nextLive = !!vesselLiveEl.checked;
      if (nextLive === vesselRunning) {
        render();
        return;
      }
      if (nextLive) {
        startVessel();
      } else {
        stopVessel();
        clearVisibleVesselMarkers();
        render();
      }
    });
    if (vesselOpacityBarEl) {
      let draggingOpacity = false;
      const clampPct = (pct) => Math.max(10, Math.min(100, pct));
      const updateOpacityVisual = (pct) => {
        const p = clampPct(pct);
        if (vesselOpacityFillEl) vesselOpacityFillEl.style.width = `${p}%`;
        if (vesselOpacityHandleEl) vesselOpacityHandleEl.style.left = `${p}%`;
        vesselOpacityBarEl.setAttribute("data-lt-vessel-opacity-value", String(Math.round(p)));
      };
      const applyOpacityPct = (pct) => {
        const p = clampPct(pct);
        state.vesselLayerOpacity = clamp(Number(p) / 100, 0.1, 1);
        saveState(stateKey, state);
        applyVesselLayerOpacity();
        updateOpacityVisual(p);
        if (vesselOpacityLabelEl) {
          vesselOpacityLabelEl.textContent = `${Math.round(p)}%`;
        }
      };
      const eventClientX = (ev) => {
        if (Number.isFinite(ev?.clientX)) return Number(ev.clientX);
        const t = ev?.touches?.[0] || ev?.changedTouches?.[0];
        return Number(t?.clientX);
      };
      const xToPct = (clientX) => {
        const r = vesselOpacityBarEl.getBoundingClientRect();
        if (!Number.isFinite(clientX) || r.width <= 0) return Math.round(getVesselLayerOpacity() * 100);
        const pct = ((clientX - r.left) / r.width) * 100;
        return clampPct(pct);
      };
      const moveOpacity = (ev) => {
        if (!draggingOpacity) return;
        applyOpacityPct(xToPct(eventClientX(ev)));
      };
      const endOpacity = (ev) => {
        if (!draggingOpacity) return;
        draggingOpacity = false;
        isAdjustingVesselOpacity = false;
        applyOpacityPct(xToPct(eventClientX(ev)));
        document.removeEventListener("pointermove", moveOpacity);
        document.removeEventListener("pointerup", endOpacity);
        document.removeEventListener("pointercancel", endOpacity);
        render();
      };
      const beginOpacity = (ev) => {
        ev.preventDefault();
        draggingOpacity = true;
        isAdjustingVesselOpacity = true;
        applyOpacityPct(xToPct(eventClientX(ev)));
        document.addEventListener("pointermove", moveOpacity);
        document.addEventListener("pointerup", endOpacity);
        document.addEventListener("pointercancel", endOpacity);
      };
      vesselOpacityBarEl.addEventListener("pointerdown", beginOpacity);
      vesselOpacityBarEl.addEventListener("mousedown", beginOpacity);
      vesselOpacityBarEl.addEventListener("touchstart", beginOpacity, { passive: false });
      applyOpacityPct(Math.round(getVesselLayerOpacity() * 100));
    } else {
      if (vesselOpacityLabelEl) {
        vesselOpacityLabelEl.textContent = `${Math.round(getVesselLayerOpacity() * 100)}%`;
      }
    }
    vesselActivityFilterEl?.addEventListener("change", () => {
      pullState();
      syncVisibleVesselMarkersFromLatest();
      if (vesselRunning) pollVesselsOnce();
      else render();
    });
    vesselFilterEl?.addEventListener("input", () => {
      state.vesselFilterText = String(vesselFilterEl.value || "");
      saveState(stateKey, state);
      render();
    });
    vesselScopeEl?.addEventListener("change", () => {
      pullState();
      renderVesselQueryBboxes(buildVesselBoundingBoxes(app.map.instance.getBounds()));
      if (vesselRunning) {
        clearVisibleVesselMarkers();
        pollVesselsOnce();
      }
      else render();
    });
    vesselColorModeEl?.addEventListener("change", () => {
      pullState();
      if (vesselRunning) pollVesselsOnce();
      else render();
    });
    vesselMoveWindowEl?.addEventListener("change", () => {
      pullState();
      if (vesselRunning) pollVesselsOnce();
      else render();
    });
    const vesselTrackHoursEl = containerEl.querySelector("#lt-vessel-track-hours");
    vesselTrackHoursEl?.addEventListener("change", () => {
      state.vesselTrackHours = clamp(Math.round(Number(vesselTrackHoursEl.value || state.vesselTrackHours || 12)), 1, 48);
      saveState(stateKey, state);
      if (trackedVessels.size) reloadAllTrackedVessels();
      else render();
    });
    const vesselTrackModeEl = containerEl.querySelector("#lt-vessel-track-mode");
    vesselTrackModeEl?.addEventListener("change", () => {
      state.vesselTrackRenderMode = String(vesselTrackModeEl.value || "decimated") === "full" ? "full" : "decimated";
      saveState(stateKey, state);
      if (trackedVessels.size) rerenderAllTrackedVesselGeometries();
      render();
    });
    if (prevFilterFocused && vesselFilterEl) {
      vesselFilterEl.focus();
      const start = Number.isFinite(prevFilterSelStart) ? prevFilterSelStart : vesselFilterEl.value.length;
      const end = Number.isFinite(prevFilterSelEnd) ? prevFilterSelEnd : vesselFilterEl.value.length;
      try {
        vesselFilterEl.setSelectionRange(start, end);
      } catch {}
    }
  }

  return {
    mount,
    destroy,
  };
}
